import { myApi as api } from "../common/redux/my-api";
export const addTagTypes = [
  "Organization",
  "Document",
  "Assessment",
  "Misc",
  "SourcingEvent",
  "Bid",
  "Upload",
  "EmailInvite",
  "Product",
  "Contract",
  "TextDocument",
  "Industry",
  "Notification",
  "Me",
  "Project",
  "Scim",
  "Search",
] as const;
const injectedRtkApi = api
  .enhanceEndpoints({
    addTagTypes,
  })
  .injectEndpoints({
    endpoints: (build) => ({
      adminListAllEntities: build.query<
        AdminListAllEntitiesApiResponse,
        AdminListAllEntitiesApiArg
      >({
        query: (queryArg) => ({
          url: `/api/admin/all-entities`,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
          params: {
            entityType: queryArg.entityType,
          },
        }),
        providesTags: ["Organization"],
      }),
      adminListOrganizations: build.query<
        AdminListOrganizationsApiResponse,
        AdminListOrganizationsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/admin/orgs`,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
          params: {
            skip: queryArg.skip,
            limit: queryArg.limit,
            name: queryArg.name,
          },
        }),
        providesTags: ["Organization"],
      }),
      adminUpdateOrganization: build.mutation<
        AdminUpdateOrganizationApiResponse,
        AdminUpdateOrganizationApiArg
      >({
        query: (queryArg) => ({
          url: `/api/admin/orgs/${queryArg.orgId}`,
          method: "PATCH",
          body: queryArg.adminUpdateOrganizationRequest,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["Organization"],
      }),
      sendAiChatMessage: build.mutation<
        SendAiChatMessageApiResponse,
        SendAiChatMessageApiArg
      >({
        query: (queryArg) => ({
          url: `/api/ai/chat`,
          method: "POST",
          body: queryArg.aiChatRequest,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["Document"],
      }),
      createAssessmentTemplate: build.mutation<
        CreateAssessmentTemplateApiResponse,
        CreateAssessmentTemplateApiArg
      >({
        query: (queryArg) => ({
          url: `/api/assessment-templates`,
          method: "POST",
          body: queryArg.createAssessmentTemplateRequestDto,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["Assessment"],
      }),
      getAssessmentTemplate: build.query<
        GetAssessmentTemplateApiResponse,
        GetAssessmentTemplateApiArg
      >({
        query: (queryArg) => ({
          url: `/api/assessment-templates/${queryArg.id}`,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        providesTags: ["Assessment"],
      }),
      createAssessment: build.mutation<
        CreateAssessmentApiResponse,
        CreateAssessmentApiArg
      >({
        query: (queryArg) => ({
          url: `/api/assessments`,
          method: "POST",
          body: queryArg.createAssessmentRequestDto,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["Assessment"],
      }),
      getAssessment: build.query<GetAssessmentApiResponse, GetAssessmentApiArg>(
        {
          query: (queryArg) => ({
            url: `/api/assessments/${queryArg.id}`,
            headers: {
              cookie: queryArg.cookie,
              organizationId: queryArg.organizationId,
              personId: queryArg.personId,
            },
          }),
          providesTags: ["Assessment"],
        },
      ),
      getBaseData: build.query<GetBaseDataApiResponse, GetBaseDataApiArg>({
        query: () => ({ url: `/api/base-data` }),
        providesTags: ["Misc"],
      }),
      getTypes: build.query<GetTypesApiResponse, GetTypesApiArg>({
        query: () => ({ url: `/api/base-data/types` }),
        providesTags: ["Misc"],
      }),
      createBasicSourcingEvent: build.mutation<
        CreateBasicSourcingEventApiResponse,
        CreateBasicSourcingEventApiArg
      >({
        query: (queryArg) => ({
          url: `/api/buyer/basic/deps/${queryArg.depId}/events`,
          method: "POST",
          body: queryArg.createSourcingEventRequest,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["SourcingEvent"],
      }),
      getNegotiationRound: build.query<
        GetNegotiationRoundApiResponse,
        GetNegotiationRoundApiArg
      >({
        query: (queryArg) => ({
          url: `/api/buyer/basic/events/negotiation/${queryArg.negotiationId}`,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        providesTags: ["SourcingEvent"],
      }),
      getBasicSourcingEvent: build.query<
        GetBasicSourcingEventApiResponse,
        GetBasicSourcingEventApiArg
      >({
        query: (queryArg) => ({
          url: `/api/buyer/basic/events/${queryArg.eventId}`,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        providesTags: ["SourcingEvent"],
      }),
      updateBasicSourcingEvent: build.mutation<
        UpdateBasicSourcingEventApiResponse,
        UpdateBasicSourcingEventApiArg
      >({
        query: (queryArg) => ({
          url: `/api/buyer/basic/events/${queryArg.eventId}`,
          method: "PUT",
          body: queryArg.editSourcingEventRequest,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["SourcingEvent"],
      }),
      awardToBid: build.mutation<AwardToBidApiResponse, AwardToBidApiArg>({
        query: (queryArg) => ({
          url: `/api/buyer/basic/events/${queryArg.eventId}/award`,
          method: "POST",
          body: queryArg.awardToBidRequest,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["SourcingEvent"],
      }),
      listReceivedBids: build.query<
        ListReceivedBidsApiResponse,
        ListReceivedBidsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/buyer/basic/events/${queryArg.eventId}/bids`,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
          params: {
            negotiationRound: queryArg.negotiationRound,
          },
        }),
        providesTags: ["Bid"],
      }),
      getReceivedBseBid: build.query<
        GetReceivedBseBidApiResponse,
        GetReceivedBseBidApiArg
      >({
        query: (queryArg) => ({
          url: `/api/buyer/basic/events/${queryArg.eventId}/bids/${queryArg.bidId}`,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        providesTags: ["Bid"],
      }),
      cancelSourcingEvent: build.mutation<
        CancelSourcingEventApiResponse,
        CancelSourcingEventApiArg
      >({
        query: (queryArg) => ({
          url: `/api/buyer/basic/events/${queryArg.eventId}/cancel`,
          method: "PUT",
          body: queryArg.cancelSourcingEventRequest,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["SourcingEvent"],
      }),
      closeSourcingEvent: build.mutation<
        CloseSourcingEventApiResponse,
        CloseSourcingEventApiArg
      >({
        query: (queryArg) => ({
          url: `/api/buyer/basic/events/${queryArg.eventId}/close`,
          method: "PUT",
          body: queryArg.closeSourcingEventRequest,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["SourcingEvent"],
      }),
      completeEvaluation: build.mutation<
        CompleteEvaluationApiResponse,
        CompleteEvaluationApiArg
      >({
        query: (queryArg) => ({
          url: `/api/buyer/basic/events/${queryArg.eventId}/complete`,
          method: "POST",
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["SourcingEvent"],
      }),
      createSourcingEventCopy: build.mutation<
        CreateSourcingEventCopyApiResponse,
        CreateSourcingEventCopyApiArg
      >({
        query: (queryArg) => ({
          url: `/api/buyer/basic/events/${queryArg.eventId}/copy`,
          method: "POST",
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["SourcingEvent"],
      }),
      createUploadForSourcingEventDescription: build.mutation<
        CreateUploadForSourcingEventDescriptionApiResponse,
        CreateUploadForSourcingEventDescriptionApiArg
      >({
        query: (queryArg) => ({
          url: `/api/buyer/basic/events/${queryArg.eventId}/description/uploads/${queryArg.uploadId}`,
          method: "POST",
          body: queryArg.createUploadRequestDto,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["Upload"],
      }),
      createEmailInviteReminder: build.mutation<
        CreateEmailInviteReminderApiResponse,
        CreateEmailInviteReminderApiArg
      >({
        query: (queryArg) => ({
          url: `/api/buyer/basic/events/${queryArg.eventId}/invites/${queryArg.inviteId}/reminders`,
          method: "POST",
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["EmailInvite"],
      }),
      createNegotiationRound: build.mutation<
        CreateNegotiationRoundApiResponse,
        CreateNegotiationRoundApiArg
      >({
        query: (queryArg) => ({
          url: `/api/buyer/basic/events/${queryArg.eventId}/negotiation`,
          method: "POST",
          body: queryArg.createNegotiationRoundRequest,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["SourcingEvent"],
      }),
      openCompletedEvent: build.mutation<
        OpenCompletedEventApiResponse,
        OpenCompletedEventApiArg
      >({
        query: (queryArg) => ({
          url: `/api/buyer/basic/events/${queryArg.eventId}/open`,
          method: "POST",
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["SourcingEvent"],
      }),
      publishBasicSourcingEvent: build.mutation<
        PublishBasicSourcingEventApiResponse,
        PublishBasicSourcingEventApiArg
      >({
        query: (queryArg) => ({
          url: `/api/buyer/basic/events/${queryArg.eventId}/publishing`,
          method: "PUT",
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["SourcingEvent"],
      }),
      createBseQuestionAsBuyer: build.mutation<
        CreateBseQuestionAsBuyerApiResponse,
        CreateBseQuestionAsBuyerApiArg
      >({
        query: (queryArg) => ({
          url: `/api/buyer/basic/events/${queryArg.eventId}/questions`,
          method: "POST",
          body: queryArg.createBseQuestionRequest,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["SourcingEvent"],
      }),
      updateBseQuestion: build.mutation<
        UpdateBseQuestionApiResponse,
        UpdateBseQuestionApiArg
      >({
        query: (queryArg) => ({
          url: `/api/buyer/basic/events/${queryArg.eventId}/questions/${queryArg.questionId}`,
          method: "PATCH",
          body: queryArg.updateBseQuestionRequest,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["SourcingEvent"],
      }),
      createBseQuestionCommentAsBuyer: build.mutation<
        CreateBseQuestionCommentAsBuyerApiResponse,
        CreateBseQuestionCommentAsBuyerApiArg
      >({
        query: (queryArg) => ({
          url: `/api/buyer/basic/events/${queryArg.eventId}/questions/${queryArg.questionId}/comments`,
          method: "POST",
          body: queryArg.createBseQuestionCommentRequest,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["SourcingEvent"],
      }),
      createUploadForBse: build.mutation<
        CreateUploadForBseApiResponse,
        CreateUploadForBseApiArg
      >({
        query: (queryArg) => ({
          url: `/api/buyer/basic/events/${queryArg.eventId}/uploads/${queryArg.uploadId}`,
          method: "POST",
          body: queryArg.createUploadRequestDto,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["Upload"],
      }),
      evaluateSingleBidWithAi: build.mutation<
        EvaluateSingleBidWithAiApiResponse,
        EvaluateSingleBidWithAiApiArg
      >({
        query: (queryArg) => ({
          url: `/api/buyer/bids/evaluate-with-ai`,
          method: "POST",
          body: queryArg.evaluateSingleBidWithAiRequest,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["Bid"],
      }),
      evaluateBidsWithAi: build.mutation<
        EvaluateBidsWithAiApiResponse,
        EvaluateBidsWithAiApiArg
      >({
        query: (queryArg) => ({
          url: `/api/buyer/events/evaluate-with-ai`,
          method: "POST",
          body: queryArg.evaluateBidsWithAiRequest,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["SourcingEvent"],
      }),
      generateRequirementsWithAi: build.mutation<
        GenerateRequirementsWithAiApiResponse,
        GenerateRequirementsWithAiApiArg
      >({
        query: (queryArg) => ({
          url: `/api/buyer/events/generate-requirements-with-ai`,
          method: "POST",
          body: queryArg.generateRequirementsWithAiRequest,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["SourcingEvent"],
      }),
      deleteSourcingEvent: build.mutation<
        DeleteSourcingEventApiResponse,
        DeleteSourcingEventApiArg
      >({
        query: (queryArg) => ({
          url: `/api/buyer/events/${queryArg.eventId}`,
          method: "DELETE",
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["SourcingEvent"],
      }),
      createOrganizationInviteEmailReminder: build.mutation<
        CreateOrganizationInviteEmailReminderApiResponse,
        CreateOrganizationInviteEmailReminderApiArg
      >({
        query: (queryArg) => ({
          url: `/api/buyer/events/${queryArg.eventId}/invites/${queryArg.inviteId}/email-invites/${queryArg.emailInviteId}/reminders`,
          method: "POST",
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["EmailInvite"],
      }),
      completeNegotiationRoundEvaluation: build.mutation<
        CompleteNegotiationRoundEvaluationApiResponse,
        CompleteNegotiationRoundEvaluationApiArg
      >({
        query: (queryArg) => ({
          url: `/api/buyer/events/${queryArg.eventId}/negotiation/${queryArg.negotiationId}/complete`,
          method: "PUT",
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["SourcingEvent"],
      }),
      openNegotiationRound: build.mutation<
        OpenNegotiationRoundApiResponse,
        OpenNegotiationRoundApiArg
      >({
        query: (queryArg) => ({
          url: `/api/buyer/events/${queryArg.eventId}/negotiation/${queryArg.negotiationId}/open`,
          method: "PUT",
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["SourcingEvent"],
      }),
      publishNegotiationRound: build.mutation<
        PublishNegotiationRoundApiResponse,
        PublishNegotiationRoundApiArg
      >({
        query: (queryArg) => ({
          url: `/api/buyer/events/${queryArg.eventId}/negotiation/${queryArg.negotiationId}/publish`,
          method: "PUT",
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["SourcingEvent"],
      }),
      editNegotiationRound: build.mutation<
        EditNegotiationRoundApiResponse,
        EditNegotiationRoundApiArg
      >({
        query: (queryArg) => ({
          url: `/api/buyer/negotiations/${queryArg.negotiationId}`,
          method: "PUT",
          body: queryArg.editNegotiationRequest,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["SourcingEvent"],
      }),
      createUploadForNegotiationRoundDescription: build.mutation<
        CreateUploadForNegotiationRoundDescriptionApiResponse,
        CreateUploadForNegotiationRoundDescriptionApiArg
      >({
        query: (queryArg) => ({
          url: `/api/buyer/negotiations/${queryArg.negotiationRoundId}/description/uploads/${queryArg.uploadId}`,
          method: "POST",
          body: queryArg.createUploadRequestDto,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["Upload"],
      }),
      createUploadForNegotiationRound: build.mutation<
        CreateUploadForNegotiationRoundApiResponse,
        CreateUploadForNegotiationRoundApiArg
      >({
        query: (queryArg) => ({
          url: `/api/buyer/negotiations/${queryArg.negotiationRoundId}/uploads/${queryArg.uploadId}`,
          method: "POST",
          body: queryArg.createUploadRequestDto,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["Upload"],
      }),
      getProductGroupsForSourcingEvent: build.query<
        GetProductGroupsForSourcingEventApiResponse,
        GetProductGroupsForSourcingEventApiArg
      >({
        query: (queryArg) => ({
          url: `/api/buyer/products/sourcing/groups/${queryArg.sourcingEventId}`,
        }),
        providesTags: ["Product"],
      }),
      listEventsForBuyer: build.query<
        ListEventsForBuyerApiResponse,
        ListEventsForBuyerApiArg
      >({
        query: (queryArg) => ({
          url: `/api/buyer/${queryArg.orgId}/events`,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
          params: {
            statuses: queryArg.statuses,
            projectIds: queryArg.projectIds,
            name: queryArg.name,
            responsiblePersons: queryArg.responsiblePersons,
            limit: queryArg.limit,
            skip: queryArg.skip,
            sortBy: queryArg.sortBy,
            sortDirection: queryArg.sortDirection,
          },
        }),
        providesTags: ["SourcingEvent"],
      }),
      listContractReports: build.query<
        ListContractReportsApiResponse,
        ListContractReportsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/contract-reports`,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
          params: {
            q: queryArg.q,
            sortBy: queryArg.sortBy,
            sortDirection: queryArg.sortDirection,
            sortByCustomField: queryArg.sortByCustomField,
          },
        }),
        providesTags: ["Contract"],
      }),
      listContractReportOptions: build.query<
        ListContractReportOptionsApiResponse,
        ListContractReportOptionsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/contract-reports/options`,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
          params: {
            q: queryArg.q,
            parameterName: queryArg.parameterName,
            optionValues: queryArg.optionValues,
            optionSearchTerm: queryArg.optionSearchTerm,
          },
        }),
        providesTags: ["Contract"],
      }),
      createContractFromSourcingEvent: build.mutation<
        CreateContractFromSourcingEventApiResponse,
        CreateContractFromSourcingEventApiArg
      >({
        query: (queryArg) => ({
          url: `/api/contract/from-sourcing-event`,
          method: "POST",
          body: queryArg.createContractFromSourcingEventRequest,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["Contract", "SourcingEvent"],
      }),
      deleteCustomContractDataField: build.mutation<
        DeleteCustomContractDataFieldApiResponse,
        DeleteCustomContractDataFieldApiArg
      >({
        query: (queryArg) => ({
          url: `/api/contract/organization/custom-field/${queryArg.orgId}`,
          method: "DELETE",
          body: queryArg.deleteCustomFieldDto,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["Contract", "Organization"],
      }),
      upsertCustomContractDataField: build.mutation<
        UpsertCustomContractDataFieldApiResponse,
        UpsertCustomContractDataFieldApiArg
      >({
        query: (queryArg) => ({
          url: `/api/contract/organization/custom-field/${queryArg.orgId}`,
          method: "PUT",
          body: queryArg.upsertCustomContractFieldDto,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["Contract"],
      }),
      upsertContractDataFieldsSection: build.mutation<
        UpsertContractDataFieldsSectionApiResponse,
        UpsertContractDataFieldsSectionApiArg
      >({
        query: (queryArg) => ({
          url: `/api/contract/organization/fields-section/${queryArg.orgId}`,
          method: "POST",
          body: queryArg.contractDataFieldSectionDto,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["Contract"],
      }),
      contractReportQueryDummy: build.mutation<
        ContractReportQueryDummyApiResponse,
        ContractReportQueryDummyApiArg
      >({
        query: (queryArg) => ({
          url: `/api/contract/report-query-dummy`,
          method: "POST",
          body: queryArg.contractReportQueryDto,
        }),
        invalidatesTags: ["Contract"],
      }),
      signatureWebhook: build.mutation<
        SignatureWebhookApiResponse,
        SignatureWebhookApiArg
      >({
        query: () => ({
          url: `/api/contract/signature/events`,
          method: "POST",
        }),
        invalidatesTags: ["Contract"],
      }),
      deleteContractTaskComment: build.mutation<
        DeleteContractTaskCommentApiResponse,
        DeleteContractTaskCommentApiArg
      >({
        query: (queryArg) => ({
          url: `/api/contract/task/comments/${queryArg.commentId}`,
          method: "DELETE",
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["Contract"],
      }),
      updateContractTaskComment: build.mutation<
        UpdateContractTaskCommentApiResponse,
        UpdateContractTaskCommentApiArg
      >({
        query: (queryArg) => ({
          url: `/api/contract/task/comments/${queryArg.commentId}`,
          method: "PATCH",
          body: queryArg.updateContractTaskCommentRequest,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["Contract"],
      }),
      deleteContractTask: build.mutation<
        DeleteContractTaskApiResponse,
        DeleteContractTaskApiArg
      >({
        query: (queryArg) => ({
          url: `/api/contract/tasks/${queryArg.taskId}`,
          method: "DELETE",
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["Contract"],
      }),
      updateContractTask: build.mutation<
        UpdateContractTaskApiResponse,
        UpdateContractTaskApiArg
      >({
        query: (queryArg) => ({
          url: `/api/contract/tasks/${queryArg.taskId}`,
          method: "PATCH",
          body: queryArg.updateTaskRequest,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["Contract"],
      }),
      listChangesForContractTask: build.query<
        ListChangesForContractTaskApiResponse,
        ListChangesForContractTaskApiArg
      >({
        query: (queryArg) => ({
          url: `/api/contract/tasks/${queryArg.taskId}/changes`,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        providesTags: ["Contract"],
      }),
      listCommentsForContractTask: build.query<
        ListCommentsForContractTaskApiResponse,
        ListCommentsForContractTaskApiArg
      >({
        query: (queryArg) => ({
          url: `/api/contract/tasks/${queryArg.taskId}/comments`,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        providesTags: ["Contract"],
      }),
      createContractTaskComment: build.mutation<
        CreateContractTaskCommentApiResponse,
        CreateContractTaskCommentApiArg
      >({
        query: (queryArg) => ({
          url: `/api/contract/tasks/${queryArg.taskId}/comments`,
          method: "POST",
          body: queryArg.createContractTaskCommentRequest,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["Contract"],
      }),
      createContractTextDocument: build.mutation<
        CreateContractTextDocumentApiResponse,
        CreateContractTextDocumentApiArg
      >({
        query: (queryArg) => ({
          url: `/api/contract/text-documents`,
          method: "POST",
          body: queryArg.createContractTextDocumentRequest,
        }),
        invalidatesTags: ["Contract", "TextDocument"],
      }),
      listTextDocumentsForContract: build.query<
        ListTextDocumentsForContractApiResponse,
        ListTextDocumentsForContractApiArg
      >({
        query: (queryArg) => ({
          url: `/api/contract/text-documents/${queryArg.contractId}`,
        }),
        providesTags: ["Contract", "TextDocument"],
      }),
      downloadMultiIfrsReport: build.query<
        DownloadMultiIfrsReportApiResponse,
        DownloadMultiIfrsReportApiArg
      >({
        query: (queryArg) => ({
          url: `/api/contracts/ifrs-report`,
          params: {
            contractIds: queryArg.contractIds,
          },
        }),
        providesTags: ["Contract"],
      }),
      getContractStatistics: build.query<
        GetContractStatisticsApiResponse,
        GetContractStatisticsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/contracts/orgs/${queryArg.orgId}/statistics`,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        providesTags: ["Contract"],
      }),
      cancelSignedDocument: build.mutation<
        CancelSignedDocumentApiResponse,
        CancelSignedDocumentApiArg
      >({
        query: (queryArg) => ({
          url: `/api/contracts/${queryArg.contractId}/cancel-signed-document`,
          method: "POST",
          body: queryArg.cancelSignedDocumentRequest,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["Contract"],
      }),
      createUploadForContractDescription: build.mutation<
        CreateUploadForContractDescriptionApiResponse,
        CreateUploadForContractDescriptionApiArg
      >({
        query: (queryArg) => ({
          url: `/api/contracts/${queryArg.contractId}/description/uploads/${queryArg.uploadId}`,
          method: "POST",
          body: queryArg.createUploadRequestDto,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["Upload"],
      }),
      downloadIfrsReport: build.query<
        DownloadIfrsReportApiResponse,
        DownloadIfrsReportApiArg
      >({
        query: (queryArg) => ({
          url: `/api/contracts/${queryArg.contractId}/ifrs-report`,
        }),
        providesTags: ["Contract"],
      }),
      deleteContractParticipant: build.mutation<
        DeleteContractParticipantApiResponse,
        DeleteContractParticipantApiArg
      >({
        query: (queryArg) => ({
          url: `/api/contracts/${queryArg.contractId}/participant`,
          method: "DELETE",
          body: queryArg.deleteContractParticipantRequest,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["Contract"],
      }),
      listParticipantsForContracts: build.query<
        ListParticipantsForContractsApiResponse,
        ListParticipantsForContractsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/contracts/${queryArg.contractId}/participants`,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        providesTags: ["Contract"],
      }),
      createContractParticipant: build.mutation<
        CreateContractParticipantApiResponse,
        CreateContractParticipantApiArg
      >({
        query: (queryArg) => ({
          url: `/api/contracts/${queryArg.contractId}/participants`,
          method: "POST",
          body: queryArg.createContractParticipantRequest,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["Contract"],
      }),
      createContractTask: build.mutation<
        CreateContractTaskApiResponse,
        CreateContractTaskApiArg
      >({
        query: (queryArg) => ({
          url: `/api/contracts/${queryArg.contractId}/tasks`,
          method: "POST",
          body: queryArg.createContractTaskRequest,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["Contract"],
      }),
      createUploadForContract: build.mutation<
        CreateUploadForContractApiResponse,
        CreateUploadForContractApiArg
      >({
        query: (queryArg) => ({
          url: `/api/contracts/${queryArg.contractId}/uploads/${queryArg.uploadId}`,
          method: "POST",
          body: queryArg.createUploadRequestDto,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["Upload"],
      }),
      listContractFilterOptions: build.query<
        ListContractFilterOptionsApiResponse,
        ListContractFilterOptionsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/deps/${queryArg.orgId}/contract/filter-options`,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        providesTags: ["Contract"],
      }),
      listContracts: build.query<ListContractsApiResponse, ListContractsApiArg>(
        {
          query: (queryArg) => ({
            url: `/api/deps/${queryArg.orgId}/contracts`,
            headers: {
              cookie: queryArg.cookie,
              organizationId: queryArg.organizationId,
              personId: queryArg.personId,
            },
            params: {
              title: queryArg.title,
              state: queryArg.state,
              statuses: queryArg.statuses,
              counterparties: queryArg.counterparties,
              owners: queryArg.owners,
              templates: queryArg.templates,
              internalparties: queryArg.internalparties,
              projects: queryArg.projects,
              limit: queryArg.limit,
              skip: queryArg.skip,
            },
          }),
          providesTags: ["Contract"],
        },
      ),
      startDocumentChat: build.mutation<
        StartDocumentChatApiResponse,
        StartDocumentChatApiArg
      >({
        query: (queryArg) => ({
          url: `/api/docs/start-chat`,
          method: "POST",
          body: queryArg.startDocumentChatRequest,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["Document"],
      }),
      updateDocumentMeta: build.mutation<
        UpdateDocumentMetaApiResponse,
        UpdateDocumentMetaApiArg
      >({
        query: (queryArg) => ({
          url: `/api/docs/${queryArg.docId}`,
          method: "PUT",
          body: queryArg.updateDocumentMetaRequest,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["Document"],
      }),
      getDownloadLink: build.query<
        GetDownloadLinkApiResponse,
        GetDownloadLinkApiArg
      >({
        query: (queryArg) => ({
          url: `/api/docs/${queryArg.docId}/link`,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        providesTags: ["Document"],
      }),
      listDocumentTemplates: build.query<
        ListDocumentTemplatesApiResponse,
        ListDocumentTemplatesApiArg
      >({
        query: () => ({ url: `/api/document-template/text-documents` }),
        providesTags: ["TextDocument"],
      }),
      createDocumentTemplate: build.mutation<
        CreateDocumentTemplateApiResponse,
        CreateDocumentTemplateApiArg
      >({
        query: (queryArg) => ({
          url: `/api/document-template/text-documents`,
          method: "POST",
          body: queryArg.createDocumentTemplateRequest,
        }),
        invalidatesTags: ["TextDocument"],
      }),
      downloadTextDocument: build.mutation<
        DownloadTextDocumentApiResponse,
        DownloadTextDocumentApiArg
      >({
        query: (queryArg) => ({
          url: `/api/download/text-documents`,
          method: "POST",
          body: queryArg.downloadTextDocumentRequest,
        }),
        invalidatesTags: ["TextDocument"],
      }),
      updateEmailStatus: build.mutation<
        UpdateEmailStatusApiResponse,
        UpdateEmailStatusApiArg
      >({
        query: () => ({ url: `/api/email/status/events`, method: "POST" }),
        invalidatesTags: ["Misc"],
      }),
      confirmUpload: build.mutation<
        ConfirmUploadApiResponse,
        ConfirmUploadApiArg
      >({
        query: () => ({
          url: `/api/file-uploads/successful-upload-webhook`,
          method: "POST",
        }),
        invalidatesTags: ["Upload"],
      }),
      searchIndustries: build.query<
        SearchIndustriesApiResponse,
        SearchIndustriesApiArg
      >({
        query: (queryArg) => ({
          url: `/api/industries`,
          params: {
            name: queryArg.name,
          },
        }),
        providesTags: ["Industry"],
      }),
      listMyNotifications: build.query<
        ListMyNotificationsApiResponse,
        ListMyNotificationsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/me/notifications`,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
          params: {
            limit: queryArg.limit,
            offset: queryArg.offset,
            unseen: queryArg.unseen,
          },
        }),
        providesTags: ["Notification"],
      }),
      listOrganizationUsers: build.query<
        ListOrganizationUsersApiResponse,
        ListOrganizationUsersApiArg
      >({
        query: (queryArg) => ({
          url: `/api/me/organization-users`,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
          params: {
            name: queryArg.name,
            limit: queryArg.limit,
            skip: queryArg.skip,
          },
        }),
        providesTags: ["Organization"],
      }),
      listOrganizationsForMe: build.query<
        ListOrganizationsForMeApiResponse,
        ListOrganizationsForMeApiArg
      >({
        query: (queryArg) => ({
          url: `/api/me/orgs`,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        providesTags: ["Organization"],
      }),
      createOrganizationForMe: build.mutation<
        CreateOrganizationForMeApiResponse,
        CreateOrganizationForMeApiArg
      >({
        query: (queryArg) => ({
          url: `/api/me/orgs`,
          method: "POST",
          body: queryArg.createOrgRequest,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["Organization"],
      }),
      markNotificationsAsPeeked: build.mutation<
        MarkNotificationsAsPeekedApiResponse,
        MarkNotificationsAsPeekedApiArg
      >({
        query: (queryArg) => ({
          url: `/api/me/peek/notifications`,
          method: "POST",
          body: queryArg.markNotificationsRequest,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["Notification"],
      }),
      markNotificationsAsViewed: build.mutation<
        MarkNotificationsAsViewedApiResponse,
        MarkNotificationsAsViewedApiArg
      >({
        query: (queryArg) => ({
          url: `/api/me/view/notifications`,
          method: "POST",
          body: queryArg.markNotificationsRequest,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["Notification"],
      }),
      createNegotiationTextDocument: build.mutation<
        CreateNegotiationTextDocumentApiResponse,
        CreateNegotiationTextDocumentApiArg
      >({
        query: (queryArg) => ({
          url: `/api/negotiation/text-documents`,
          method: "POST",
          body: queryArg.createNegotiationTextDocumentRequest,
        }),
        invalidatesTags: ["SourcingEvent", "TextDocument"],
      }),
      listTextDocumentsForNegotiationRound: build.query<
        ListTextDocumentsForNegotiationRoundApiResponse,
        ListTextDocumentsForNegotiationRoundApiArg
      >({
        query: (queryArg) => ({
          url: `/api/negotiation/text-documents/${queryArg.negotiationRoundId}`,
        }),
        providesTags: ["TextDocument"],
      }),
      getAccessToken: build.mutation<
        GetAccessTokenApiResponse,
        GetAccessTokenApiArg
      >({
        query: (queryArg) => ({
          url: `/api/org/access-token`,
          method: "POST",
          body: queryArg.getAccessTokenRequest,
        }),
        invalidatesTags: ["Organization"],
      }),
      searchOpenOrgContactInfo: build.query<
        SearchOpenOrgContactInfoApiResponse,
        SearchOpenOrgContactInfoApiArg
      >({
        query: (queryArg) => ({
          url: `/api/org/contact-info`,
          params: {
            name: queryArg.name,
            number: queryArg["number"],
          },
        }),
        providesTags: ["Organization"],
      }),
      listContractsForOrg: build.query<
        ListContractsForOrgApiResponse,
        ListContractsForOrgApiArg
      >({
        query: (queryArg) => ({
          url: `/api/org/contracts`,
          params: {
            title: queryArg.title,
            state: queryArg.state,
            statuses: queryArg.statuses,
            counterparties: queryArg.counterparties,
            owners: queryArg.owners,
            templates: queryArg.templates,
            internalparties: queryArg.internalparties,
            projects: queryArg.projects,
            limit: queryArg.limit,
            skip: queryArg.skip,
          },
        }),
        providesTags: ["Contract"],
      }),
      createApiClient: build.mutation<
        CreateApiClientApiResponse,
        CreateApiClientApiArg
      >({
        query: (queryArg) => ({
          url: `/api/org/create-api-client`,
          method: "POST",
          headers: {
            cookie: queryArg.cookie,
          },
        }),
        invalidatesTags: ["Organization"],
      }),
      createOrganizationEntry: build.mutation<
        CreateOrganizationEntryApiResponse,
        CreateOrganizationEntryApiArg
      >({
        query: (queryArg) => ({
          url: `/api/org/entries`,
          method: "POST",
          body: queryArg.createOrganizationEntryRequest,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["Organization"],
      }),
      getOrganizationEntry: build.query<
        GetOrganizationEntryApiResponse,
        GetOrganizationEntryApiArg
      >({
        query: (queryArg) => ({
          url: `/api/org/entries/${queryArg.entryId}`,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        providesTags: ["Organization"],
      }),
      updateOrganizationEntry: build.mutation<
        UpdateOrganizationEntryApiResponse,
        UpdateOrganizationEntryApiArg
      >({
        query: (queryArg) => ({
          url: `/api/org/entries/${queryArg.entryId}`,
          method: "PUT",
          body: queryArg.updateOrganizationEntryRequest,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["Organization"],
      }),
      linkOrganizationEntry: build.mutation<
        LinkOrganizationEntryApiResponse,
        LinkOrganizationEntryApiArg
      >({
        query: (queryArg) => ({
          url: `/api/org/entries/${queryArg.entryId}/link`,
          method: "PUT",
          body: queryArg.linkOrganizationEntryRequest,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["Organization"],
      }),
      getSupplierEmailInvite: build.query<
        GetSupplierEmailInviteApiResponse,
        GetSupplierEmailInviteApiArg
      >({
        query: (queryArg) => ({
          url: `/api/org/supplier-invites/${queryArg.inviteId}`,
          params: {
            secret: queryArg.secret,
          },
        }),
        providesTags: ["EmailInvite"],
      }),
      getOrgUserEmailInvite: build.query<
        GetOrgUserEmailInviteApiResponse,
        GetOrgUserEmailInviteApiArg
      >({
        query: (queryArg) => ({
          url: `/api/org/user-invites/${queryArg.inviteId}`,
          params: {
            secret: queryArg.secret,
          },
        }),
        providesTags: ["EmailInvite"],
      }),
      acceptOrgUserEmailInvite: build.mutation<
        AcceptOrgUserEmailInviteApiResponse,
        AcceptOrgUserEmailInviteApiArg
      >({
        query: (queryArg) => ({
          url: `/api/org/user-invites/${queryArg.inviteId}/acceptance`,
          method: "PUT",
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
          params: {
            secret: queryArg.secret,
          },
        }),
        invalidatesTags: ["EmailInvite"],
      }),
      listAssessmentTemplates: build.query<
        ListAssessmentTemplatesApiResponse,
        ListAssessmentTemplatesApiArg
      >({
        query: (queryArg) => ({
          url: `/api/orgs/assessment-templates`,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        providesTags: ["Assessment"],
      }),
      updateAssessment: build.mutation<
        UpdateAssessmentApiResponse,
        UpdateAssessmentApiArg
      >({
        query: (queryArg) => ({
          url: `/api/orgs/assessments/${queryArg.id}`,
          method: "PUT",
          body: queryArg.updateAssessmentRequestDto,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["Assessment"],
      }),
      listAssessments: build.query<
        ListAssessmentsApiResponse,
        ListAssessmentsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/orgs/assessments/${queryArg.orgId}`,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        providesTags: ["Assessment"],
      }),
      updateOrganization: build.mutation<
        UpdateOrganizationApiResponse,
        UpdateOrganizationApiArg
      >({
        query: (queryArg) => ({
          url: `/api/orgs/${queryArg.orgId}`,
          method: "PATCH",
          body: queryArg.editOrganizationRequest,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["Organization"],
      }),
      getOrganization: build.query<
        GetOrganizationApiResponse,
        GetOrganizationApiArg
      >({
        query: (queryArg) => ({
          url: `/api/orgs/${queryArg.orgId}/public-profile`,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        providesTags: ["Organization"],
      }),
      listSupplierEmailInvites: build.query<
        ListSupplierEmailInvitesApiResponse,
        ListSupplierEmailInvitesApiArg
      >({
        query: (queryArg) => ({
          url: `/api/orgs/${queryArg.orgId}/supplier-invites`,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        providesTags: ["EmailInvite"],
      }),
      createSupplierEmailInvite: build.mutation<
        CreateSupplierEmailInviteApiResponse,
        CreateSupplierEmailInviteApiArg
      >({
        query: (queryArg) => ({
          url: `/api/orgs/${queryArg.orgId}/supplier-invites`,
          method: "POST",
          body: queryArg.createEmailInviteRequest,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["EmailInvite"],
      }),
      deleteSupplierEmailInvite: build.mutation<
        DeleteSupplierEmailInviteApiResponse,
        DeleteSupplierEmailInviteApiArg
      >({
        query: (queryArg) => ({
          url: `/api/orgs/${queryArg.orgId}/supplier-invites/${queryArg.inviteId}`,
          method: "DELETE",
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["EmailInvite"],
      }),
      createSupplierEmailInviteReminder: build.mutation<
        CreateSupplierEmailInviteReminderApiResponse,
        CreateSupplierEmailInviteReminderApiArg
      >({
        query: (queryArg) => ({
          url: `/api/orgs/${queryArg.orgId}/supplier-invites/${queryArg.inviteId}/reminders`,
          method: "POST",
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["EmailInvite"],
      }),
      updateOrganizationTenant: build.mutation<
        UpdateOrganizationTenantApiResponse,
        UpdateOrganizationTenantApiArg
      >({
        query: (queryArg) => ({
          url: `/api/orgs/${queryArg.orgId}/tenant`,
          method: "PUT",
          body: queryArg.updateOrgTenantRequest,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["Organization"],
      }),
      listOrgUserEmailInvites: build.query<
        ListOrgUserEmailInvitesApiResponse,
        ListOrgUserEmailInvitesApiArg
      >({
        query: (queryArg) => ({
          url: `/api/orgs/${queryArg.orgId}/user-invites`,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        providesTags: ["EmailInvite"],
      }),
      createOrgUserEmailInvite: build.mutation<
        CreateOrgUserEmailInviteApiResponse,
        CreateOrgUserEmailInviteApiArg
      >({
        query: (queryArg) => ({
          url: `/api/orgs/${queryArg.orgId}/user-invites`,
          method: "POST",
          body: queryArg.createOrgUserEmailInviteRequest,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["EmailInvite"],
      }),
      deleteOrgUserEmailInvite: build.mutation<
        DeleteOrgUserEmailInviteApiResponse,
        DeleteOrgUserEmailInviteApiArg
      >({
        query: (queryArg) => ({
          url: `/api/orgs/${queryArg.orgId}/user-invites/${queryArg.inviteId}`,
          method: "DELETE",
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["EmailInvite"],
      }),
      deleteOrgUser: build.mutation<
        DeleteOrgUserApiResponse,
        DeleteOrgUserApiArg
      >({
        query: (queryArg) => ({
          url: `/api/orgs/${queryArg.orgId}/users/${queryArg.userId}`,
          method: "PUT",
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["Organization"],
      }),
      updateOrgUserRole: build.mutation<
        UpdateOrgUserRoleApiResponse,
        UpdateOrgUserRoleApiArg
      >({
        query: (queryArg) => ({
          url: `/api/orgs/${queryArg.orgId}/users/${queryArg.userId}/role`,
          method: "PUT",
          body: queryArg.updateOrgUserRoleRequest,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["Organization"],
      }),
      listContractTasksForOrganization: build.query<
        ListContractTasksForOrganizationApiResponse,
        ListContractTasksForOrganizationApiArg
      >({
        query: (queryArg) => ({
          url: `/api/owner/contract/orgs/${queryArg.orgId}/tasks`,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
          params: {
            limit: queryArg.limit,
            statuses: queryArg.statuses,
            personIds: queryArg.personIds,
            contractIds: queryArg.contractIds,
          },
        }),
        providesTags: ["Contract"],
      }),
      listContractTemplates: build.query<
        ListContractTemplatesApiResponse,
        ListContractTemplatesApiArg
      >({
        query: (queryArg) => ({
          url: `/api/owner/contract/orgs/${queryArg.orgId}/templates`,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
          params: {
            contractId: queryArg.contractId,
          },
        }),
        providesTags: ["Contract"],
      }),
      createContractTemplate: build.mutation<
        CreateContractTemplateApiResponse,
        CreateContractTemplateApiArg
      >({
        query: (queryArg) => ({
          url: `/api/owner/contract/orgs/${queryArg.orgId}/templates`,
          method: "POST",
          body: queryArg.upsertContractTemplateRequest,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["Contract"],
      }),
      getTask: build.query<GetTaskApiResponse, GetTaskApiArg>({
        query: (queryArg) => ({
          url: `/api/owner/contract/tasks/${queryArg.taskId}`,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        providesTags: ["Contract"],
      }),
      updateContractTemplate: build.mutation<
        UpdateContractTemplateApiResponse,
        UpdateContractTemplateApiArg
      >({
        query: (queryArg) => ({
          url: `/api/owner/contract/templates/${queryArg.templateId}`,
          method: "PATCH",
          body: queryArg.upsertContractTemplateRequest,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["Contract"],
      }),
      getContract: build.query<GetContractApiResponse, GetContractApiArg>({
        query: (queryArg) => ({
          url: `/api/owner/contracts/${queryArg.contractId}`,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        providesTags: ["Contract"],
      }),
      updateContract: build.mutation<
        UpdateContractApiResponse,
        UpdateContractApiArg
      >({
        query: (queryArg) => ({
          url: `/api/owner/contracts/${queryArg.contractId}`,
          method: "POST",
          body: queryArg.updateContractRequest,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["Contract"],
      }),
      publishContract: build.mutation<
        PublishContractApiResponse,
        PublishContractApiArg
      >({
        query: (queryArg) => ({
          url: `/api/owner/contracts/${queryArg.contractId}/publishing`,
          method: "PUT",
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["Contract"],
      }),
      createSignedDocument: build.mutation<
        CreateSignedDocumentApiResponse,
        CreateSignedDocumentApiArg
      >({
        query: (queryArg) => ({
          url: `/api/owner/contracts/${queryArg.contractId}/signed-documents`,
          method: "POST",
          body: queryArg.createSignedDocumentRequest,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["Contract"],
      }),
      createSignedTextDocument: build.mutation<
        CreateSignedTextDocumentApiResponse,
        CreateSignedTextDocumentApiArg
      >({
        query: (queryArg) => ({
          url: `/api/owner/contracts/${queryArg.contractId}/signed-text-documents`,
          method: "POST",
          body: queryArg.createSignedTextDocumentRequest,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["Contract"],
      }),
      listContractTasks: build.query<
        ListContractTasksApiResponse,
        ListContractTasksApiArg
      >({
        query: (queryArg) => ({
          url: `/api/owner/contracts/${queryArg.contractId}/tasks`,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        providesTags: ["Contract"],
      }),
      createContract: build.mutation<
        CreateContractApiResponse,
        CreateContractApiArg
      >({
        query: (queryArg) => ({
          url: `/api/owner/deps/${queryArg.depId}/contracts`,
          method: "POST",
          body: queryArg.createContractRequest,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["Contract"],
      }),
      listPersons: build.query<ListPersonsApiResponse, ListPersonsApiArg>({
        query: (queryArg) => ({
          url: `/api/persons`,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
          params: {
            orgIds: queryArg.orgIds,
            name: queryArg.name,
            roles: queryArg.roles,
            limit: queryArg.limit,
            skip: queryArg.skip,
          },
        }),
        providesTags: ["Organization"],
      }),
      getPersonsByIds: build.query<
        GetPersonsByIdsApiResponse,
        GetPersonsByIdsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/persons-by-ids`,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
          params: {
            personIds: queryArg.personIds,
          },
        }),
        providesTags: ["Organization"],
      }),
      getMe: build.query<GetMeApiResponse, GetMeApiArg>({
        query: (queryArg) => ({
          url: `/api/persons/me`,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        providesTags: ["Me"],
      }),
      updateMe: build.mutation<UpdateMeApiResponse, UpdateMeApiArg>({
        query: (queryArg) => ({
          url: `/api/persons/me`,
          method: "PATCH",
          body: queryArg.editMeRequest,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["Me"],
      }),
      editProductGroup: build.mutation<
        EditProductGroupApiResponse,
        EditProductGroupApiArg
      >({
        query: (queryArg) => ({
          url: `/api/products/group`,
          method: "PATCH",
          body: queryArg.editProductGroupDto,
        }),
        invalidatesTags: ["Product", "SourcingEvent"],
      }),
      createProductGroup: build.mutation<
        CreateProductGroupApiResponse,
        CreateProductGroupApiArg
      >({
        query: (queryArg) => ({
          url: `/api/products/group`,
          method: "POST",
          body: queryArg.createProductGroupDto,
        }),
        invalidatesTags: ["Product", "SourcingEvent"],
      }),
      deleteProductGroup: build.mutation<
        DeleteProductGroupApiResponse,
        DeleteProductGroupApiArg
      >({
        query: (queryArg) => ({
          url: `/api/products/group/${queryArg.productGroupId}`,
          method: "DELETE",
        }),
        invalidatesTags: ["Product", "SourcingEvent"],
      }),
      getProductGroupsForProject: build.query<
        GetProductGroupsForProjectApiResponse,
        GetProductGroupsForProjectApiArg
      >({
        query: (queryArg) => ({
          url: `/api/products/project/groups/${queryArg.projectId}`,
        }),
        providesTags: ["Product", "Project"],
      }),
      getProductTemplates: build.query<
        GetProductTemplatesApiResponse,
        GetProductTemplatesApiArg
      >({
        query: () => ({ url: `/api/products/templates` }),
        providesTags: ["Product"],
      }),
      updateProject: build.mutation<
        UpdateProjectApiResponse,
        UpdateProjectApiArg
      >({
        query: (queryArg) => ({
          url: `/api/project`,
          method: "PATCH",
          body: queryArg.updateProjectRequest,
        }),
        invalidatesTags: ["Project"],
      }),
      createProject: build.mutation<
        CreateProjectApiResponse,
        CreateProjectApiArg
      >({
        query: (queryArg) => ({
          url: `/api/project`,
          method: "POST",
          body: queryArg.createProjectRequest,
        }),
        invalidatesTags: ["Project"],
      }),
      createProjectTextDocument: build.mutation<
        CreateProjectTextDocumentApiResponse,
        CreateProjectTextDocumentApiArg
      >({
        query: (queryArg) => ({
          url: `/api/project/text-document`,
          method: "POST",
          body: queryArg.createProjectTextDocumentRequest,
        }),
        invalidatesTags: ["Project", "TextDocument"],
      }),
      listTextDocumentsForProject: build.query<
        ListTextDocumentsForProjectApiResponse,
        ListTextDocumentsForProjectApiArg
      >({
        query: (queryArg) => ({
          url: `/api/project/text-documents/${queryArg.projectId}`,
        }),
        providesTags: ["Project", "TextDocument"],
      }),
      getProject: build.query<GetProjectApiResponse, GetProjectApiArg>({
        query: (queryArg) => ({ url: `/api/project/${queryArg.projectId}` }),
        providesTags: ["Project"],
      }),
      getProjects: build.query<GetProjectsApiResponse, GetProjectsApiArg>({
        query: (queryArg) => ({
          url: `/api/projects`,
          params: {
            name: queryArg.name,
            status: queryArg.status,
            limit: queryArg.limit,
            skip: queryArg.skip,
          },
        }),
        providesTags: ["Project"],
      }),
      getProjectsByIds: build.query<
        GetProjectsByIdsApiResponse,
        GetProjectsByIdsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/projects-by-ids`,
          params: {
            projectIds: queryArg.projectIds,
          },
        }),
        providesTags: ["Project"],
      }),
      createUploadForProjectDescription: build.mutation<
        CreateUploadForProjectDescriptionApiResponse,
        CreateUploadForProjectDescriptionApiArg
      >({
        query: (queryArg) => ({
          url: `/api/projects/${queryArg.projectId}/description/uploads/${queryArg.uploadId}`,
          method: "POST",
          body: queryArg.createUploadRequestDto,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["Upload"],
      }),
      createUploadForProject: build.mutation<
        CreateUploadForProjectApiResponse,
        CreateUploadForProjectApiArg
      >({
        query: (queryArg) => ({
          url: `/api/projects/${queryArg.projectId}/uploads/${queryArg.uploadId}`,
          method: "POST",
          body: queryArg.createUploadRequestDto,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["Upload"],
      }),
      getGroups: build.query<GetGroupsApiResponse, GetGroupsApiArg>({
        query: (queryArg) => ({
          url: `/api/scim/Groups`,
          headers: {
            authorization: queryArg.authorization,
          },
        }),
        providesTags: ["Scim"],
      }),
      createGroup: build.mutation<CreateGroupApiResponse, CreateGroupApiArg>({
        query: (queryArg) => ({
          url: `/api/scim/Groups`,
          method: "POST",
          body: queryArg.createGroupDto,
          headers: {
            authorization: queryArg.authorization,
          },
        }),
        invalidatesTags: ["Scim"],
      }),
      deleteGroup: build.mutation<DeleteGroupApiResponse, DeleteGroupApiArg>({
        query: (queryArg) => ({
          url: `/api/scim/Groups/${queryArg.groupId}`,
          method: "DELETE",
          headers: {
            authorization: queryArg.authorization,
          },
        }),
        invalidatesTags: ["Scim"],
      }),
      getGroup: build.query<GetGroupApiResponse, GetGroupApiArg>({
        query: (queryArg) => ({
          url: `/api/scim/Groups/${queryArg.groupId}`,
          headers: {
            authorization: queryArg.authorization,
          },
          params: {
            count: queryArg.count,
            startIndex: queryArg.startIndex,
          },
        }),
        providesTags: ["Scim"],
      }),
      updateGroup: build.mutation<UpdateGroupApiResponse, UpdateGroupApiArg>({
        query: (queryArg) => ({
          url: `/api/scim/Groups/${queryArg.groupId}`,
          method: "PATCH",
          body: queryArg.updateGroupRequest,
          headers: {
            authorization: queryArg.authorization,
          },
        }),
        invalidatesTags: ["Scim"],
      }),
      getSchemas: build.query<GetSchemasApiResponse, GetSchemasApiArg>({
        query: () => ({ url: `/api/scim/Schemas` }),
        providesTags: ["Scim"],
      }),
      getToken: build.query<GetTokenApiResponse, GetTokenApiArg>({
        query: (queryArg) => ({
          url: `/api/scim/Token`,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
            authorization: queryArg.authorization,
          },
        }),
        providesTags: ["Scim"],
      }),
      getUsers: build.query<GetUsersApiResponse, GetUsersApiArg>({
        query: (queryArg) => ({
          url: `/api/scim/Users`,
          headers: {
            authorization: queryArg.authorization,
          },
        }),
        providesTags: ["Scim"],
      }),
      createUser: build.mutation<CreateUserApiResponse, CreateUserApiArg>({
        query: (queryArg) => ({
          url: `/api/scim/Users`,
          method: "POST",
          body: queryArg.createUserDto,
          headers: {
            authorization: queryArg.authorization,
          },
        }),
        invalidatesTags: ["Scim"],
      }),
      deleteUser: build.mutation<DeleteUserApiResponse, DeleteUserApiArg>({
        query: (queryArg) => ({
          url: `/api/scim/Users/${queryArg.userId}`,
          method: "DELETE",
          headers: {
            authorization: queryArg.authorization,
          },
        }),
        invalidatesTags: ["Scim"],
      }),
      getUser: build.query<GetUserApiResponse, GetUserApiArg>({
        query: (queryArg) => ({
          url: `/api/scim/Users/${queryArg.userId}`,
          headers: {
            authorization: queryArg.authorization,
          },
        }),
        providesTags: ["Scim"],
      }),
      updateUser: build.mutation<UpdateUserApiResponse, UpdateUserApiArg>({
        query: (queryArg) => ({
          url: `/api/scim/Users/${queryArg.userId}`,
          method: "PATCH",
          headers: {
            authorization: queryArg.authorization,
          },
        }),
        invalidatesTags: ["Scim"],
      }),
      searchOrganizationsAndEntries: build.query<
        SearchOrganizationsAndEntriesApiResponse,
        SearchOrganizationsAndEntriesApiArg
      >({
        query: (queryArg) => ({
          url: `/api/search/orgs`,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
          params: {
            name: queryArg.name,
            limit: queryArg.limit,
            skip: queryArg.skip,
            type: queryArg["type"],
            excludeLinkedEntries: queryArg.excludeLinkedEntries,
          },
        }),
        providesTags: ["Organization", "Search"],
      }),
      updateSignedDocumentMeta: build.mutation<
        UpdateSignedDocumentMetaApiResponse,
        UpdateSignedDocumentMetaApiArg
      >({
        query: (queryArg) => ({
          url: `/api/signed/docs/${queryArg.docId}`,
          method: "PUT",
          body: queryArg.updateSignedDocumentMetaRequest,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["Document"],
      }),
      getSignedDocumentDownloadLink: build.query<
        GetSignedDocumentDownloadLinkApiResponse,
        GetSignedDocumentDownloadLinkApiArg
      >({
        query: (queryArg) => ({
          url: `/api/signed/docs/${queryArg.docId}/link`,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        providesTags: ["Document"],
      }),
      getSignedAttachmentDownloadLink: build.query<
        GetSignedAttachmentDownloadLinkApiResponse,
        GetSignedAttachmentDownloadLinkApiArg
      >({
        query: (queryArg) => ({
          url: `/api/signed/docs/${queryArg.docId}/link/${queryArg.attachmentId}`,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        providesTags: ["Document"],
      }),
      createSourcingEventTextDocument: build.mutation<
        CreateSourcingEventTextDocumentApiResponse,
        CreateSourcingEventTextDocumentApiArg
      >({
        query: (queryArg) => ({
          url: `/api/sourcing-event/text-documents`,
          method: "POST",
          body: queryArg.createSourcingEventTextDocumentRequest,
        }),
        invalidatesTags: ["SourcingEvent", "TextDocument"],
      }),
      listTextDocumentsForSourcingEvent: build.query<
        ListTextDocumentsForSourcingEventApiResponse,
        ListTextDocumentsForSourcingEventApiArg
      >({
        query: (queryArg) => ({
          url: `/api/sourcing-event/text-documents/${queryArg.eventId}`,
        }),
        providesTags: ["TextDocument"],
      }),
      getBseBid: build.query<GetBseBidApiResponse, GetBseBidApiArg>({
        query: (queryArg) => ({
          url: `/api/supplier/basic/bids/${queryArg.bidId}`,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        providesTags: ["Bid"],
      }),
      updateBseBid: build.mutation<UpdateBseBidApiResponse, UpdateBseBidApiArg>(
        {
          query: (queryArg) => ({
            url: `/api/supplier/basic/bids/${queryArg.bidId}`,
            method: "PATCH",
            body: queryArg.editBidRequest,
            headers: {
              cookie: queryArg.cookie,
              organizationId: queryArg.organizationId,
              personId: queryArg.personId,
            },
          }),
          invalidatesTags: ["Bid"],
        },
      ),
      createUploadForBseBidDescription: build.mutation<
        CreateUploadForBseBidDescriptionApiResponse,
        CreateUploadForBseBidDescriptionApiArg
      >({
        query: (queryArg) => ({
          url: `/api/supplier/basic/bids/${queryArg.bidId}/description/uploads/${queryArg.uploadId}`,
          method: "POST",
          body: queryArg.createUploadRequestDto,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["Upload"],
      }),
      createUploadForBseBid: build.mutation<
        CreateUploadForBseBidApiResponse,
        CreateUploadForBseBidApiArg
      >({
        query: (queryArg) => ({
          url: `/api/supplier/basic/bids/${queryArg.bidId}/uploads/${queryArg.uploadId}`,
          method: "POST",
          body: queryArg.createUploadRequestDto,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["Upload"],
      }),
      getBseSupplierView: build.query<
        GetBseSupplierViewApiResponse,
        GetBseSupplierViewApiArg
      >({
        query: (queryArg) => ({
          url: `/api/supplier/basic/events/${queryArg.eventId}`,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        providesTags: ["SourcingEvent"],
      }),
      createBseAnnouncement: build.mutation<
        CreateBseAnnouncementApiResponse,
        CreateBseAnnouncementApiArg
      >({
        query: (queryArg) => ({
          url: `/api/supplier/basic/events/${queryArg.eventId}/announcements`,
          method: "POST",
          body: queryArg.createBseAnnouncementRequest,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["SourcingEvent"],
      }),
      unDeliverBseBid: build.mutation<
        UnDeliverBseBidApiResponse,
        UnDeliverBseBidApiArg
      >({
        query: (queryArg) => ({
          url: `/api/supplier/basic/events/${queryArg.eventId}/bids/${queryArg.bidId}/delivery`,
          method: "DELETE",
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["Bid"],
      }),
      acceptInvitationToBid: build.mutation<
        AcceptInvitationToBidApiResponse,
        AcceptInvitationToBidApiArg
      >({
        query: (queryArg) => ({
          url: `/api/supplier/basic/events/${queryArg.eventId}/invites`,
          method: "PATCH",
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["SourcingEvent"],
      }),
      declineInvitationToBid: build.mutation<
        DeclineInvitationToBidApiResponse,
        DeclineInvitationToBidApiArg
      >({
        query: (queryArg) => ({
          url: `/api/supplier/basic/events/${queryArg.eventId}/invites/decline`,
          method: "PATCH",
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["SourcingEvent"],
      }),
      downloadPublicProcurementProtocolPartOne: build.mutation<
        DownloadPublicProcurementProtocolPartOneApiResponse,
        DownloadPublicProcurementProtocolPartOneApiArg
      >({
        query: (queryArg) => ({
          url: `/api/supplier/basic/events/${queryArg.eventId}/public-procurement-protocol-part-one`,
          method: "POST",
          body: queryArg.getProcurementProtocolRequest,
        }),
        invalidatesTags: ["SourcingEvent"],
      }),
      downloadQaReport: build.query<
        DownloadQaReportApiResponse,
        DownloadQaReportApiArg
      >({
        query: (queryArg) => ({
          url: `/api/supplier/basic/events/${queryArg.eventId}/qa-report`,
        }),
        providesTags: ["SourcingEvent"],
      }),
      createBseQuestionAsSupplier: build.mutation<
        CreateBseQuestionAsSupplierApiResponse,
        CreateBseQuestionAsSupplierApiArg
      >({
        query: (queryArg) => ({
          url: `/api/supplier/basic/events/${queryArg.eventId}/questions`,
          method: "POST",
          body: queryArg.createBseQuestionRequest,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["SourcingEvent"],
      }),
      createBseQuestionCommentAsSupplier: build.mutation<
        CreateBseQuestionCommentAsSupplierApiResponse,
        CreateBseQuestionCommentAsSupplierApiArg
      >({
        query: (queryArg) => ({
          url: `/api/supplier/basic/events/${queryArg.eventId}/questions/${queryArg.questionId}/comments`,
          method: "POST",
          body: queryArg.createBseQuestionCommentRequest,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["SourcingEvent"],
      }),
      markAsViewed: build.mutation<MarkAsViewedApiResponse, MarkAsViewedApiArg>(
        {
          query: (queryArg) => ({
            url: `/api/supplier/basic/events/${queryArg.eventId}/viewing`,
            method: "PUT",
            headers: {
              cookie: queryArg.cookie,
              organizationId: queryArg.organizationId,
              personId: queryArg.personId,
            },
          }),
          invalidatesTags: ["SourcingEvent"],
        },
      ),
      createDraftBid: build.mutation<
        CreateDraftBidApiResponse,
        CreateDraftBidApiArg
      >({
        query: (queryArg) => ({
          url: `/api/supplier/bids`,
          method: "POST",
          body: queryArg.createDraftBidRequest,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["Bid"],
      }),
      deleteBid: build.mutation<DeleteBidApiResponse, DeleteBidApiArg>({
        query: (queryArg) => ({
          url: `/api/supplier/bids/${queryArg.bidId}`,
          method: "DELETE",
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["Bid"],
      }),
      listBidsCreatedByDepartment: build.query<
        ListBidsCreatedByDepartmentApiResponse,
        ListBidsCreatedByDepartmentApiArg
      >({
        query: (queryArg) => ({
          url: `/api/supplier/deps/${queryArg.depId}/bids`,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        providesTags: ["Bid"],
      }),
      getSeEmailInvite: build.query<
        GetSeEmailInviteApiResponse,
        GetSeEmailInviteApiArg
      >({
        query: (queryArg) => ({
          url: `/api/supplier/event/invites/${queryArg.inviteId}`,
          params: {
            secret: queryArg.secret,
          },
        }),
        providesTags: ["EmailInvite"],
      }),
      acceptSeEmailInvite: build.mutation<
        AcceptSeEmailInviteApiResponse,
        AcceptSeEmailInviteApiArg
      >({
        query: (queryArg) => ({
          url: `/api/supplier/event/invites/${queryArg.inviteId}/acceptance`,
          method: "PUT",
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
          params: {
            secret: queryArg.secret,
          },
        }),
        invalidatesTags: ["EmailInvite"],
      }),
      deliverBid: build.mutation<DeliverBidApiResponse, DeliverBidApiArg>({
        query: (queryArg) => ({
          url: `/api/supplier/events/bids/delivery`,
          method: "PUT",
          body: queryArg.deliverBidRequest,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["Bid"],
      }),
      listSeSupplierViews: build.query<
        ListSeSupplierViewsApiResponse,
        ListSeSupplierViewsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/supplier/orgs/${queryArg.orgId}/events`,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
          params: {
            statuses: queryArg.statuses,
          },
        }),
        providesTags: ["SourcingEvent"],
      }),
      getProductGroupsAsSupplier: build.query<
        GetProductGroupsAsSupplierApiResponse,
        GetProductGroupsAsSupplierApiArg
      >({
        query: (queryArg) => ({
          url: `/api/supplier/products/sourcing/groups/${queryArg.sourcingEventId}`,
        }),
        providesTags: ["Product", "SourcingEvent"],
      }),
      updateTextDocument: build.mutation<
        UpdateTextDocumentApiResponse,
        UpdateTextDocumentApiArg
      >({
        query: (queryArg) => ({
          url: `/api/text-documents`,
          method: "PUT",
          body: queryArg.updateTextDocumentRequest,
        }),
        invalidatesTags: ["TextDocument"],
      }),
      deleteTextDocument: build.mutation<
        DeleteTextDocumentApiResponse,
        DeleteTextDocumentApiArg
      >({
        query: (queryArg) => ({
          url: `/api/text-documents/${queryArg.id}`,
          method: "DELETE",
        }),
        invalidatesTags: ["TextDocument"],
      }),
      createUploadForTextDocument: build.mutation<
        CreateUploadForTextDocumentApiResponse,
        CreateUploadForTextDocumentApiArg
      >({
        query: (queryArg) => ({
          url: `/api/text-documents/${queryArg.textDocumentId}/uploads/${queryArg.uploadId}`,
          method: "POST",
          body: queryArg.createUploadRequestDto,
          headers: {
            cookie: queryArg.cookie,
            organizationId: queryArg.organizationId,
            personId: queryArg.personId,
          },
        }),
        invalidatesTags: ["Upload"],
      }),
      deleteUpload: build.mutation<DeleteUploadApiResponse, DeleteUploadApiArg>(
        {
          query: (queryArg) => ({
            url: `/api/uploads/${queryArg.uploadId}`,
            method: "DELETE",
            headers: {
              cookie: queryArg.cookie,
              organizationId: queryArg.organizationId,
              personId: queryArg.personId,
            },
          }),
          invalidatesTags: ["Upload"],
        },
      ),
    }),
    overrideExisting: false,
  });
export { injectedRtkApi as bffApi };
export type AdminListAllEntitiesApiResponse = /** status 200 OK */ string;
export type AdminListAllEntitiesApiArg = {
  entityType: AdminEntityType;
  cookie?: string;
  organizationId?: string;
  personId?: string;
};
export type AdminListOrganizationsApiResponse =
  /** status 200 OK */ AdminListOrganizationsResponse;
export type AdminListOrganizationsApiArg = {
  skip?: number;
  limit?: number;
  name?: string;
  cookie?: string;
  organizationId?: string;
  personId?: string;
};
export type AdminUpdateOrganizationApiResponse = /** status 200 OK */ OrgDto;
export type AdminUpdateOrganizationApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  orgId: string;
  adminUpdateOrganizationRequest: AdminUpdateOrganizationRequest;
};
export type SendAiChatMessageApiResponse = /** status 200 OK */ AiChatResponse;
export type SendAiChatMessageApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  aiChatRequest: AiChatRequest;
};
export type CreateAssessmentTemplateApiResponse =
  /** status 201 Created */ AssessmentTemplateDto;
export type CreateAssessmentTemplateApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  createAssessmentTemplateRequestDto: CreateAssessmentTemplateRequestDto;
};
export type GetAssessmentTemplateApiResponse =
  /** status 200 OK */ AssessmentTemplateDto;
export type GetAssessmentTemplateApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  id: string;
};
export type CreateAssessmentApiResponse =
  /** status 201 Created */ AssessmentDto;
export type CreateAssessmentApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  createAssessmentRequestDto: CreateAssessmentRequestDto;
};
export type GetAssessmentApiResponse = /** status 200 OK */ AssessmentDto;
export type GetAssessmentApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  id: string;
};
export type GetBaseDataApiResponse = /** status 200 OK */ BaseDataDto;
export type GetBaseDataApiArg = void;
export type GetTypesApiResponse = /** status 200 OK */ AllTypes;
export type GetTypesApiArg = void;
export type CreateBasicSourcingEventApiResponse = /** status 200 OK */ BseDto;
export type CreateBasicSourcingEventApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  depId: string;
  createSourcingEventRequest: CreateSourcingEventRequest;
};
export type GetNegotiationRoundApiResponse =
  /** status 200 OK */ NegotiationRoundDto;
export type GetNegotiationRoundApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  negotiationId: string;
};
export type GetBasicSourcingEventApiResponse = /** status 200 OK */ BseDto;
export type GetBasicSourcingEventApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  eventId: string;
};
export type UpdateBasicSourcingEventApiResponse = /** status 200 OK */ BseDto;
export type UpdateBasicSourcingEventApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  eventId: string;
  editSourcingEventRequest: EditSourcingEventRequest;
};
export type AwardToBidApiResponse = /** status 200 OK */ BseDto;
export type AwardToBidApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  eventId: string;
  awardToBidRequest: AwardToBidRequest;
};
export type ListReceivedBidsApiResponse =
  /** status 200 OK */ ListReceivedBidsResponse;
export type ListReceivedBidsApiArg = {
  negotiationRound?: string;
  cookie?: string;
  organizationId?: string;
  personId?: string;
  eventId: string;
};
export type GetReceivedBseBidApiResponse =
  /** status 200 OK */ ReceivedBseBidDto;
export type GetReceivedBseBidApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  eventId: string;
  bidId: string;
};
export type CancelSourcingEventApiResponse = /** status 200 OK */ BseDto;
export type CancelSourcingEventApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  eventId: string;
  cancelSourcingEventRequest: CancelSourcingEventRequest;
};
export type CloseSourcingEventApiResponse = /** status 200 OK */ BseDto;
export type CloseSourcingEventApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  eventId: string;
  closeSourcingEventRequest: CloseSourcingEventRequest;
};
export type CompleteEvaluationApiResponse = /** status 200 OK */ BseDto;
export type CompleteEvaluationApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  eventId: string;
};
export type CreateSourcingEventCopyApiResponse = /** status 200 OK */ BseDto;
export type CreateSourcingEventCopyApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  eventId: string;
};
export type CreateUploadForSourcingEventDescriptionApiResponse =
  /** status 200 OK */ UploadDto;
export type CreateUploadForSourcingEventDescriptionApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  eventId: string;
  uploadId: string;
  createUploadRequestDto: CreateUploadRequestDto;
};
export type CreateEmailInviteReminderApiResponse = unknown;
export type CreateEmailInviteReminderApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  eventId: string;
  inviteId: string;
};
export type CreateNegotiationRoundApiResponse =
  /** status 200 OK */ NegotiationRoundDto;
export type CreateNegotiationRoundApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  eventId: string;
  createNegotiationRoundRequest: CreateNegotiationRoundRequest;
};
export type OpenCompletedEventApiResponse = /** status 200 OK */ BseDto;
export type OpenCompletedEventApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  eventId: string;
};
export type PublishBasicSourcingEventApiResponse = /** status 200 OK */ BseDto;
export type PublishBasicSourcingEventApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  eventId: string;
};
export type CreateBseQuestionAsBuyerApiResponse = /** status 200 OK */ BseDto;
export type CreateBseQuestionAsBuyerApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  eventId: string;
  createBseQuestionRequest: CreateBseQuestionRequest;
};
export type UpdateBseQuestionApiResponse = /** status 200 OK */ BseDto;
export type UpdateBseQuestionApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  eventId: string;
  questionId: string;
  updateBseQuestionRequest: UpdateBseQuestionRequest;
};
export type CreateBseQuestionCommentAsBuyerApiResponse =
  /** status 200 OK */ BseDto;
export type CreateBseQuestionCommentAsBuyerApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  eventId: string;
  questionId: string;
  createBseQuestionCommentRequest: CreateBseQuestionCommentRequest;
};
export type CreateUploadForBseApiResponse = /** status 200 OK */ UploadDto;
export type CreateUploadForBseApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  eventId: string;
  uploadId: string;
  createUploadRequestDto: CreateUploadRequestDto;
};
export type EvaluateSingleBidWithAiApiResponse =
  /** status 200 OK */ AiChatResponse;
export type EvaluateSingleBidWithAiApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  evaluateSingleBidWithAiRequest: EvaluateSingleBidWithAiRequest;
};
export type EvaluateBidsWithAiApiResponse = /** status 200 OK */ AiChatResponse;
export type EvaluateBidsWithAiApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  evaluateBidsWithAiRequest: EvaluateBidsWithAiRequest;
};
export type GenerateRequirementsWithAiApiResponse =
  /** status 200 OK */ AiChatResponse;
export type GenerateRequirementsWithAiApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  generateRequirementsWithAiRequest: GenerateRequirementsWithAiRequest;
};
export type DeleteSourcingEventApiResponse = unknown;
export type DeleteSourcingEventApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  eventId: string;
};
export type CreateOrganizationInviteEmailReminderApiResponse = unknown;
export type CreateOrganizationInviteEmailReminderApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  eventId: string;
  inviteId: string;
  emailInviteId: string;
};
export type CompleteNegotiationRoundEvaluationApiResponse =
  /** status 200 OK */ NegotiationRoundDto;
export type CompleteNegotiationRoundEvaluationApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  eventId: string;
  negotiationId: string;
};
export type OpenNegotiationRoundApiResponse =
  /** status 200 OK */ NegotiationRoundDto;
export type OpenNegotiationRoundApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  eventId: string;
  negotiationId: string;
};
export type PublishNegotiationRoundApiResponse =
  /** status 200 OK */ NegotiationRoundDto;
export type PublishNegotiationRoundApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  eventId: string;
  negotiationId: string;
};
export type EditNegotiationRoundApiResponse =
  /** status 200 OK */ NegotiationRoundDto;
export type EditNegotiationRoundApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  negotiationId: string;
  editNegotiationRequest: EditNegotiationRequest;
};
export type CreateUploadForNegotiationRoundDescriptionApiResponse =
  /** status 200 OK */ UploadDto;
export type CreateUploadForNegotiationRoundDescriptionApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  negotiationRoundId: string;
  uploadId: string;
  createUploadRequestDto: CreateUploadRequestDto;
};
export type CreateUploadForNegotiationRoundApiResponse =
  /** status 200 OK */ UploadDto;
export type CreateUploadForNegotiationRoundApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  negotiationRoundId: string;
  uploadId: string;
  createUploadRequestDto: CreateUploadRequestDto;
};
export type GetProductGroupsForSourcingEventApiResponse =
  /** status 200 OK */ ProductGroupsDto;
export type GetProductGroupsForSourcingEventApiArg = {
  sourcingEventId: string;
};
export type ListEventsForBuyerApiResponse =
  /** status 200 OK */ ListSeDtosResponse;
export type ListEventsForBuyerApiArg = {
  statuses?: string[];
  projectIds?: string[];
  name?: string;
  responsiblePersons?: string[];
  limit?: number;
  skip?: number;
  sortBy?: SourcingEventSortField;
  sortDirection?: SortDirection;
  cookie?: string;
  organizationId?: string;
  personId?: string;
  orgId: string;
};
export type ListContractReportsApiResponse =
  /** status 200 OK */ ListContractReportsResponseDto;
export type ListContractReportsApiArg = {
  q: string;
  sortBy?: ContractReportSortColumn;
  sortDirection?: string;
  sortByCustomField?: string;
  cookie?: string;
  organizationId?: string;
  personId?: string;
};
export type ListContractReportOptionsApiResponse =
  /** status 200 OK */ ListContractReportOptionsResponseDto;
export type ListContractReportOptionsApiArg = {
  q: string;
  parameterName: ContractReportOptionParameter;
  optionValues?: string[];
  optionSearchTerm?: string;
  cookie?: string;
  organizationId?: string;
  personId?: string;
};
export type CreateContractFromSourcingEventApiResponse =
  /** status 200 OK */ ContractDto;
export type CreateContractFromSourcingEventApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  createContractFromSourcingEventRequest: CreateContractFromSourcingEventRequest;
};
export type DeleteCustomContractDataFieldApiResponse = unknown;
export type DeleteCustomContractDataFieldApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  orgId: string;
  deleteCustomFieldDto: DeleteCustomFieldDto;
};
export type UpsertCustomContractDataFieldApiResponse = unknown;
export type UpsertCustomContractDataFieldApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  orgId: string;
  upsertCustomContractFieldDto: UpsertCustomContractFieldDto;
};
export type UpsertContractDataFieldsSectionApiResponse = unknown;
export type UpsertContractDataFieldsSectionApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  orgId: string;
  contractDataFieldSectionDto: ContractDataFieldSectionDto;
};
export type ContractReportQueryDummyApiResponse = unknown;
export type ContractReportQueryDummyApiArg = {
  contractReportQueryDto: ContractReportQueryDto;
};
export type SignatureWebhookApiResponse = unknown;
export type SignatureWebhookApiArg = void;
export type DeleteContractTaskCommentApiResponse =
  /** status 200 OK */ ContractTaskCommentDto;
export type DeleteContractTaskCommentApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  commentId: string;
};
export type UpdateContractTaskCommentApiResponse =
  /** status 200 OK */ ContractTaskCommentDto;
export type UpdateContractTaskCommentApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  commentId: string;
  updateContractTaskCommentRequest: UpdateContractTaskCommentRequest;
};
export type DeleteContractTaskApiResponse = /** status 200 OK */ ContractDto;
export type DeleteContractTaskApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  taskId: string;
};
export type UpdateContractTaskApiResponse =
  /** status 200 OK */ ContractTaskDto;
export type UpdateContractTaskApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  taskId: string;
  updateTaskRequest: UpdateTaskRequest;
};
export type ListChangesForContractTaskApiResponse =
  /** status 200 OK */ ListChangesForContractTaskResponse;
export type ListChangesForContractTaskApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  taskId: string;
};
export type ListCommentsForContractTaskApiResponse =
  /** status 200 OK */ ListCommentsForContractTaskResponse;
export type ListCommentsForContractTaskApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  taskId: string;
};
export type CreateContractTaskCommentApiResponse =
  /** status 200 OK */ ContractTaskCommentDto;
export type CreateContractTaskCommentApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  taskId: string;
  createContractTaskCommentRequest: CreateContractTaskCommentRequest;
};
export type CreateContractTextDocumentApiResponse =
  /** status 201 Created */ TextDocumentDto;
export type CreateContractTextDocumentApiArg = {
  createContractTextDocumentRequest: CreateContractTextDocumentRequest;
};
export type ListTextDocumentsForContractApiResponse =
  /** status 200 OK */ TextDocumentsDto;
export type ListTextDocumentsForContractApiArg = {
  contractId: string;
};
export type DownloadMultiIfrsReportApiResponse = unknown;
export type DownloadMultiIfrsReportApiArg = {
  contractIds: string;
};
export type GetContractStatisticsApiResponse =
  /** status 200 OK */ ContractStatisticsDto;
export type GetContractStatisticsApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  orgId: string;
};
export type CancelSignedDocumentApiResponse = unknown;
export type CancelSignedDocumentApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  contractId: string;
  cancelSignedDocumentRequest: CancelSignedDocumentRequest;
};
export type CreateUploadForContractDescriptionApiResponse =
  /** status 200 OK */ UploadDto;
export type CreateUploadForContractDescriptionApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  contractId: string;
  uploadId: string;
  createUploadRequestDto: CreateUploadRequestDto;
};
export type DownloadIfrsReportApiResponse = unknown;
export type DownloadIfrsReportApiArg = {
  contractId: string;
};
export type DeleteContractParticipantApiResponse = unknown;
export type DeleteContractParticipantApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  contractId: string;
  deleteContractParticipantRequest: DeleteContractParticipantRequest;
};
export type ListParticipantsForContractsApiResponse =
  /** status 200 OK */ ListParticipantsForContractResponse;
export type ListParticipantsForContractsApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  contractId: string;
};
export type CreateContractParticipantApiResponse =
  /** status 200 OK */ ContractDto;
export type CreateContractParticipantApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  contractId: string;
  createContractParticipantRequest: CreateContractParticipantRequest;
};
export type CreateContractTaskApiResponse = /** status 200 OK */ ContractDto;
export type CreateContractTaskApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  contractId: string;
  createContractTaskRequest: CreateContractTaskRequest;
};
export type CreateUploadForContractApiResponse = /** status 200 OK */ UploadDto;
export type CreateUploadForContractApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  contractId: string;
  uploadId: string;
  createUploadRequestDto: CreateUploadRequestDto;
};
export type ListContractFilterOptionsApiResponse =
  /** status 200 OK */ ContractFilterOptionsDto;
export type ListContractFilterOptionsApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  orgId: string;
};
export type ListContractsApiResponse =
  /** status 200 OK */ ListContractsResponseDto;
export type ListContractsApiArg = {
  title?: string;
  state?: ContractStateDto;
  statuses?: string;
  counterparties?: string;
  owners?: string;
  templates?: string;
  internalparties?: string;
  projects?: string;
  limit: number;
  skip?: number;
  cookie?: string;
  organizationId?: string;
  personId?: string;
  orgId: string;
};
export type StartDocumentChatApiResponse = /** status 200 OK */ AiChatResponse;
export type StartDocumentChatApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  startDocumentChatRequest: StartDocumentChatRequest;
};
export type UpdateDocumentMetaApiResponse =
  /** status 200 OK */ ViewDocumentAsOwnerDto;
export type UpdateDocumentMetaApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  docId: string;
  updateDocumentMetaRequest: UpdateDocumentMetaRequest;
};
export type GetDownloadLinkApiResponse =
  /** status 200 OK */ GetDownloadLinkResponse;
export type GetDownloadLinkApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  docId: string;
};
export type ListDocumentTemplatesApiResponse =
  /** status 200 OK */ TextDocumentsDto;
export type ListDocumentTemplatesApiArg = void;
export type CreateDocumentTemplateApiResponse =
  /** status 201 Created */ TextDocumentDto;
export type CreateDocumentTemplateApiArg = {
  createDocumentTemplateRequest: CreateDocumentTemplateRequest;
};
export type DownloadTextDocumentApiResponse = unknown;
export type DownloadTextDocumentApiArg = {
  downloadTextDocumentRequest: DownloadTextDocumentRequest;
};
export type UpdateEmailStatusApiResponse = unknown;
export type UpdateEmailStatusApiArg = void;
export type ConfirmUploadApiResponse = unknown;
export type ConfirmUploadApiArg = void;
export type SearchIndustriesApiResponse =
  /** status 200 OK */ SearchIndustriesResponse;
export type SearchIndustriesApiArg = {
  name?: string;
};
export type ListMyNotificationsApiResponse =
  /** status 200 OK */ ListNotificationsResponse;
export type ListMyNotificationsApiArg = {
  limit?: number;
  offset?: number;
  unseen?: boolean;
  cookie?: string;
  organizationId?: string;
  personId?: string;
};
export type ListOrganizationUsersApiResponse =
  /** status 200 OK */ ListOrganizationUsersResponse;
export type ListOrganizationUsersApiArg = {
  name?: string;
  limit: number;
  skip?: number;
  cookie?: string;
  organizationId?: string;
  personId?: string;
};
export type ListOrganizationsForMeApiResponse =
  /** status 200 OK */ ListOrganizationsForMeResponse;
export type ListOrganizationsForMeApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
};
export type CreateOrganizationForMeApiResponse = /** status 200 OK */ OrgDto;
export type CreateOrganizationForMeApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  createOrgRequest: CreateOrgRequest;
};
export type MarkNotificationsAsPeekedApiResponse = unknown;
export type MarkNotificationsAsPeekedApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  markNotificationsRequest: MarkNotificationsRequest;
};
export type MarkNotificationsAsViewedApiResponse = unknown;
export type MarkNotificationsAsViewedApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  markNotificationsRequest: MarkNotificationsRequest;
};
export type CreateNegotiationTextDocumentApiResponse =
  /** status 201 Created */ TextDocumentDto;
export type CreateNegotiationTextDocumentApiArg = {
  createNegotiationTextDocumentRequest: CreateNegotiationTextDocumentRequest;
};
export type ListTextDocumentsForNegotiationRoundApiResponse =
  /** status 200 OK */ TextDocumentsDto;
export type ListTextDocumentsForNegotiationRoundApiArg = {
  negotiationRoundId: string;
};
export type GetAccessTokenApiResponse = /** status 200 OK */ AccessTokenDto;
export type GetAccessTokenApiArg = {
  getAccessTokenRequest: GetAccessTokenRequest;
};
export type SearchOpenOrgContactInfoApiResponse =
  /** status 200 OK */ SearchOrganizationResponse;
export type SearchOpenOrgContactInfoApiArg = {
  name?: string;
  number?: string;
};
export type ListContractsForOrgApiResponse =
  /** status 200 OK */ ListContractsResponseDto;
export type ListContractsForOrgApiArg = {
  title?: string;
  state?: ContractStateDto;
  statuses?: string;
  counterparties?: string;
  owners?: string;
  templates?: string;
  internalparties?: string;
  projects?: string;
  limit: number;
  skip?: number;
};
export type CreateApiClientApiResponse =
  /** status 200 OK */ CreateApiClientResponse;
export type CreateApiClientApiArg = {
  cookie?: string;
};
export type CreateOrganizationEntryApiResponse =
  /** status 200 OK */ OrganizationEntryDto;
export type CreateOrganizationEntryApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  createOrganizationEntryRequest: CreateOrganizationEntryRequest;
};
export type GetOrganizationEntryApiResponse =
  /** status 200 OK */ OrganizationEntryDto;
export type GetOrganizationEntryApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  entryId: string;
};
export type UpdateOrganizationEntryApiResponse =
  /** status 200 OK */ OrganizationEntryDto;
export type UpdateOrganizationEntryApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  entryId: string;
  updateOrganizationEntryRequest: UpdateOrganizationEntryRequest;
};
export type LinkOrganizationEntryApiResponse =
  /** status 200 OK */ OrganizationEntryDto;
export type LinkOrganizationEntryApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  entryId: string;
  linkOrganizationEntryRequest: LinkOrganizationEntryRequest;
};
export type GetSupplierEmailInviteApiResponse =
  /** status 200 OK */ SupplierEmailInviteDto;
export type GetSupplierEmailInviteApiArg = {
  secret: string;
  inviteId: string;
};
export type GetOrgUserEmailInviteApiResponse =
  /** status 200 OK */ OrgUserEmailInviteDto;
export type GetOrgUserEmailInviteApiArg = {
  secret: string;
  inviteId: string;
};
export type AcceptOrgUserEmailInviteApiResponse = /** status 200 OK */ OrgDto;
export type AcceptOrgUserEmailInviteApiArg = {
  secret: string;
  cookie?: string;
  organizationId?: string;
  personId?: string;
  inviteId: string;
};
export type ListAssessmentTemplatesApiResponse =
  /** status 200 OK */ AssessmentTemplatesDto;
export type ListAssessmentTemplatesApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
};
export type UpdateAssessmentApiResponse =
  /** status 200 Updated assessment */ AssessmentDto;
export type UpdateAssessmentApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  id: string;
  updateAssessmentRequestDto: UpdateAssessmentRequestDto;
};
export type ListAssessmentsApiResponse = /** status 200 OK */ AssessmentsDto;
export type ListAssessmentsApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  orgId: string;
};
export type UpdateOrganizationApiResponse = /** status 200 OK */ OrgDto;
export type UpdateOrganizationApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  orgId: string;
  editOrganizationRequest: EditOrganizationRequest;
};
export type GetOrganizationApiResponse =
  /** status 200 OK */ OrgPublicProfileDto;
export type GetOrganizationApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  orgId: string;
};
export type ListSupplierEmailInvitesApiResponse =
  /** status 200 OK */ ListOrganizationInvitesResponse;
export type ListSupplierEmailInvitesApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  orgId: string;
};
export type CreateSupplierEmailInviteApiResponse =
  /** status 200 OK */ SupplierEmailInviteDto;
export type CreateSupplierEmailInviteApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  orgId: string;
  createEmailInviteRequest: CreateEmailInviteRequest;
};
export type DeleteSupplierEmailInviteApiResponse = unknown;
export type DeleteSupplierEmailInviteApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  orgId: string;
  inviteId: string;
};
export type CreateSupplierEmailInviteReminderApiResponse = unknown;
export type CreateSupplierEmailInviteReminderApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  orgId: string;
  inviteId: string;
};
export type UpdateOrganizationTenantApiResponse = /** status 200 OK */ OrgDto;
export type UpdateOrganizationTenantApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  orgId: string;
  updateOrgTenantRequest: UpdateOrgTenantRequest;
};
export type ListOrgUserEmailInvitesApiResponse =
  /** status 200 OK */ ListOrgUserEmailInvitesResponse;
export type ListOrgUserEmailInvitesApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  orgId: string;
};
export type CreateOrgUserEmailInviteApiResponse =
  /** status 200 OK */ SupplierEmailInviteDto;
export type CreateOrgUserEmailInviteApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  orgId: string;
  createOrgUserEmailInviteRequest: CreateOrgUserEmailInviteRequest;
};
export type DeleteOrgUserEmailInviteApiResponse = unknown;
export type DeleteOrgUserEmailInviteApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  orgId: string;
  inviteId: string;
};
export type DeleteOrgUserApiResponse = /** status 200 OK */ OrgDto;
export type DeleteOrgUserApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  orgId: string;
  userId: string;
};
export type UpdateOrgUserRoleApiResponse = /** status 200 OK */ OrgDto;
export type UpdateOrgUserRoleApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  orgId: string;
  userId: string;
  updateOrgUserRoleRequest: UpdateOrgUserRoleRequest;
};
export type ListContractTasksForOrganizationApiResponse =
  /** status 200 OK */ ListContractTasksResponseDto;
export type ListContractTasksForOrganizationApiArg = {
  limit?: number;
  statuses?: string;
  personIds?: string;
  contractIds?: string;
  cookie?: string;
  organizationId?: string;
  personId?: string;
  orgId: string;
};
export type ListContractTemplatesApiResponse =
  /** status 200 OK */ ListContractTemplatesResponseDto;
export type ListContractTemplatesApiArg = {
  contractId?: string;
  cookie?: string;
  organizationId?: string;
  personId?: string;
  orgId: string;
};
export type CreateContractTemplateApiResponse =
  /** status 200 OK */ ContractTemplateDto;
export type CreateContractTemplateApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  orgId: string;
  upsertContractTemplateRequest: UpsertContractTemplateRequest;
};
export type GetTaskApiResponse = /** status 200 OK */ ContractTaskDto;
export type GetTaskApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  taskId: string;
};
export type UpdateContractTemplateApiResponse =
  /** status 200 OK */ ContractTemplateDto;
export type UpdateContractTemplateApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  templateId: string;
  upsertContractTemplateRequest: UpsertContractTemplateRequest;
};
export type GetContractApiResponse = /** status 200 OK */ ContractDto;
export type GetContractApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  contractId: string;
};
export type UpdateContractApiResponse = /** status 200 OK */ ContractDto;
export type UpdateContractApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  contractId: string;
  updateContractRequest: UpdateContractRequest;
};
export type PublishContractApiResponse = /** status 200 OK */ ContractDto;
export type PublishContractApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  contractId: string;
};
export type CreateSignedDocumentApiResponse = /** status 200 OK */ ContractDto;
export type CreateSignedDocumentApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  contractId: string;
  createSignedDocumentRequest: CreateSignedDocumentRequest;
};
export type CreateSignedTextDocumentApiResponse =
  /** status 200 OK */ ContractDto;
export type CreateSignedTextDocumentApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  contractId: string;
  createSignedTextDocumentRequest: CreateSignedTextDocumentRequest;
};
export type ListContractTasksApiResponse =
  /** status 200 OK */ ListContractTasksResponseDto;
export type ListContractTasksApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  contractId: string;
};
export type CreateContractApiResponse = /** status 200 OK */ ContractDto;
export type CreateContractApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  depId: string;
  createContractRequest: CreateContractRequest;
};
export type ListPersonsApiResponse = /** status 200 OK */ ListPersonsResponse;
export type ListPersonsApiArg = {
  orgIds: string[];
  name?: string;
  roles?: (
    | "Owner"
    | "Admin"
    | "SourcingCreator"
    | "SourcingViewer"
    | "ContractCreator"
    | "ContractViewer"
    | "ProjectViewer"
    | "ProjectCreator"
  )[];
  limit: number;
  skip?: number;
  cookie?: string;
  organizationId?: string;
  personId?: string;
};
export type GetPersonsByIdsApiResponse =
  /** status 200 OK */ ListPersonsByIdsResponse;
export type GetPersonsByIdsApiArg = {
  personIds: string[];
  cookie?: string;
  organizationId?: string;
  personId?: string;
};
export type GetMeApiResponse = /** status 200 OK */ MeDto;
export type GetMeApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
};
export type UpdateMeApiResponse = /** status 200 OK */ MeDto;
export type UpdateMeApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  editMeRequest: EditMeRequest;
};
export type EditProductGroupApiResponse = /** status 200 OK */ ProductGroupDto;
export type EditProductGroupApiArg = {
  editProductGroupDto: EditProductGroupDto;
};
export type CreateProductGroupApiResponse =
  /** status 201 Created */ ProductGroupDto;
export type CreateProductGroupApiArg = {
  createProductGroupDto: CreateProductGroupDto;
};
export type DeleteProductGroupApiResponse = unknown;
export type DeleteProductGroupApiArg = {
  productGroupId: string;
};
export type GetProductGroupsForProjectApiResponse =
  /** status 200 OK */ ProductGroupsDto;
export type GetProductGroupsForProjectApiArg = {
  projectId: string;
};
export type GetProductTemplatesApiResponse =
  /** status 200 OK */ ProductTemplatesDto;
export type GetProductTemplatesApiArg = void;
export type UpdateProjectApiResponse = /** status 200 OK */ ProjectDto;
export type UpdateProjectApiArg = {
  updateProjectRequest: UpdateProjectRequest;
};
export type CreateProjectApiResponse = /** status 201 Created */ ProjectDto;
export type CreateProjectApiArg = {
  createProjectRequest: CreateProjectRequest;
};
export type CreateProjectTextDocumentApiResponse =
  /** status 201 Created */ TextDocumentDto;
export type CreateProjectTextDocumentApiArg = {
  createProjectTextDocumentRequest: CreateProjectTextDocumentRequest;
};
export type ListTextDocumentsForProjectApiResponse =
  /** status 200 OK */ TextDocumentsDto;
export type ListTextDocumentsForProjectApiArg = {
  projectId: string;
};
export type GetProjectApiResponse = /** status 200 OK */ ProjectDto;
export type GetProjectApiArg = {
  projectId: string;
};
export type GetProjectsApiResponse = /** status 200 OK */ ProjectsDto;
export type GetProjectsApiArg = {
  name?: string;
  status?: string;
  limit?: number;
  skip?: number;
};
export type GetProjectsByIdsApiResponse = /** status 200 OK */ ProjectsDto;
export type GetProjectsByIdsApiArg = {
  projectIds: string[];
};
export type CreateUploadForProjectDescriptionApiResponse =
  /** status 200 OK */ UploadDto;
export type CreateUploadForProjectDescriptionApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  projectId: string;
  uploadId: string;
  createUploadRequestDto: CreateUploadRequestDto;
};
export type CreateUploadForProjectApiResponse = /** status 200 OK */ UploadDto;
export type CreateUploadForProjectApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  projectId: string;
  uploadId: string;
  createUploadRequestDto: CreateUploadRequestDto;
};
export type GetGroupsApiResponse = /** status 200 OK */ GroupsDto;
export type GetGroupsApiArg = {
  authorization?: string;
};
export type CreateGroupApiResponse = /** status 200 OK */ GroupDto;
export type CreateGroupApiArg = {
  authorization?: string;
  createGroupDto: CreateGroupDto;
};
export type DeleteGroupApiResponse = unknown;
export type DeleteGroupApiArg = {
  authorization?: string;
  groupId: string;
};
export type GetGroupApiResponse = /** status 200 OK */ GroupDto;
export type GetGroupApiArg = {
  count?: number;
  startIndex?: number;
  authorization?: string;
  groupId: string;
};
export type UpdateGroupApiResponse = /** status 200 OK */ GroupDto;
export type UpdateGroupApiArg = {
  authorization?: string;
  groupId: string;
  updateGroupRequest: UpdateGroupRequest;
};
export type GetSchemasApiResponse = /** status 200 OK */ SchemasDto;
export type GetSchemasApiArg = void;
export type GetTokenApiResponse = /** status 200 OK */ TokenResponse;
export type GetTokenApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  authorization?: string;
};
export type GetUsersApiResponse = /** status 200 OK */ UsersDto;
export type GetUsersApiArg = {
  authorization?: string;
};
export type CreateUserApiResponse = /** status 200 OK */ UserDto;
export type CreateUserApiArg = {
  authorization?: string;
  createUserDto: CreateUserDto;
};
export type DeleteUserApiResponse = unknown;
export type DeleteUserApiArg = {
  authorization?: string;
  userId: string;
};
export type GetUserApiResponse = /** status 200 OK */ UserDto;
export type GetUserApiArg = {
  authorization?: string;
  userId: string;
};
export type UpdateUserApiResponse = /** status 200 OK */ UserDto;
export type UpdateUserApiArg = {
  authorization?: string;
  userId: string;
};
export type SearchOrganizationsAndEntriesApiResponse =
  /** status 200 OK */ SearchOrganizationsAndEntriesResponse;
export type SearchOrganizationsAndEntriesApiArg = {
  name?: string;
  limit: number;
  skip?: number;
  type?: OrganizationType;
  excludeLinkedEntries?: boolean;
  cookie?: string;
  organizationId?: string;
  personId?: string;
};
export type UpdateSignedDocumentMetaApiResponse =
  /** status 200 OK */ SignedDocumentDto;
export type UpdateSignedDocumentMetaApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  docId: string;
  updateSignedDocumentMetaRequest: UpdateSignedDocumentMetaRequest;
};
export type GetSignedDocumentDownloadLinkApiResponse =
  /** status 200 OK */ GetDownloadLinkResponse;
export type GetSignedDocumentDownloadLinkApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  docId: string;
};
export type GetSignedAttachmentDownloadLinkApiResponse =
  /** status 200 OK */ GetDownloadLinkResponse;
export type GetSignedAttachmentDownloadLinkApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  docId: string;
  attachmentId: string;
};
export type CreateSourcingEventTextDocumentApiResponse =
  /** status 201 Created */ TextDocumentDto;
export type CreateSourcingEventTextDocumentApiArg = {
  createSourcingEventTextDocumentRequest: CreateSourcingEventTextDocumentRequest;
};
export type ListTextDocumentsForSourcingEventApiResponse =
  /** status 200 OK */ TextDocumentsDto;
export type ListTextDocumentsForSourcingEventApiArg = {
  eventId: string;
};
export type GetBseBidApiResponse = /** status 200 OK */ BidViewForSupplierDto;
export type GetBseBidApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  bidId: string;
};
export type UpdateBseBidApiResponse =
  /** status 200 OK */ BidViewForSupplierDto;
export type UpdateBseBidApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  bidId: string;
  editBidRequest: EditBidRequest;
};
export type CreateUploadForBseBidDescriptionApiResponse =
  /** status 200 OK */ UploadDto;
export type CreateUploadForBseBidDescriptionApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  bidId: string;
  uploadId: string;
  createUploadRequestDto: CreateUploadRequestDto;
};
export type CreateUploadForBseBidApiResponse = /** status 200 OK */ UploadDto;
export type CreateUploadForBseBidApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  bidId: string;
  uploadId: string;
  createUploadRequestDto: CreateUploadRequestDto;
};
export type GetBseSupplierViewApiResponse =
  /** status 200 OK */ SourcingEventSupplierView;
export type GetBseSupplierViewApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  eventId: string;
};
export type CreateBseAnnouncementApiResponse = /** status 200 OK */ BseDto;
export type CreateBseAnnouncementApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  eventId: string;
  createBseAnnouncementRequest: CreateBseAnnouncementRequest;
};
export type UnDeliverBseBidApiResponse =
  /** status 200 OK */ BidViewForSupplierDto;
export type UnDeliverBseBidApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  eventId: string;
  bidId: string;
};
export type AcceptInvitationToBidApiResponse =
  /** status 200 OK */ SourcingEventSupplierView;
export type AcceptInvitationToBidApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  eventId: string;
};
export type DeclineInvitationToBidApiResponse =
  /** status 200 OK */ SourcingEventSupplierView;
export type DeclineInvitationToBidApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  eventId: string;
};
export type DownloadPublicProcurementProtocolPartOneApiResponse = unknown;
export type DownloadPublicProcurementProtocolPartOneApiArg = {
  eventId: string;
  getProcurementProtocolRequest: GetProcurementProtocolRequest;
};
export type DownloadQaReportApiResponse = unknown;
export type DownloadQaReportApiArg = {
  eventId: string;
};
export type CreateBseQuestionAsSupplierApiResponse =
  /** status 200 OK */ SourcingEventSupplierView;
export type CreateBseQuestionAsSupplierApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  eventId: string;
  createBseQuestionRequest: CreateBseQuestionRequest;
};
export type CreateBseQuestionCommentAsSupplierApiResponse =
  /** status 200 OK */ SourcingEventSupplierView;
export type CreateBseQuestionCommentAsSupplierApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  eventId: string;
  questionId: string;
  createBseQuestionCommentRequest: CreateBseQuestionCommentRequest;
};
export type MarkAsViewedApiResponse =
  /** status 200 OK */ SourcingEventSupplierView;
export type MarkAsViewedApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  eventId: string;
};
export type CreateDraftBidApiResponse =
  /** status 200 OK */ BidViewForSupplierDto;
export type CreateDraftBidApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  createDraftBidRequest: CreateDraftBidRequest;
};
export type DeleteBidApiResponse = unknown;
export type DeleteBidApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  bidId: string;
};
export type ListBidsCreatedByDepartmentApiResponse =
  /** status 200 OK */ ListBidsResponse;
export type ListBidsCreatedByDepartmentApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  depId: string;
};
export type GetSeEmailInviteApiResponse = /** status 200 OK */ SeEmailInviteDto;
export type GetSeEmailInviteApiArg = {
  secret: string;
  inviteId: string;
};
export type AcceptSeEmailInviteApiResponse = unknown;
export type AcceptSeEmailInviteApiArg = {
  secret: string;
  cookie?: string;
  organizationId?: string;
  personId?: string;
  inviteId: string;
};
export type DeliverBidApiResponse = /** status 200 OK */ BidViewForSupplierDto;
export type DeliverBidApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  deliverBidRequest: DeliverBidRequest;
};
export type ListSeSupplierViewsApiResponse =
  /** status 200 OK */ ListSeSupplierViewsResponse;
export type ListSeSupplierViewsApiArg = {
  statuses?: string[];
  cookie?: string;
  organizationId?: string;
  personId?: string;
  orgId: string;
};
export type GetProductGroupsAsSupplierApiResponse =
  /** status 200 OK */ SupplierProductGroupsDto;
export type GetProductGroupsAsSupplierApiArg = {
  sourcingEventId: string;
};
export type UpdateTextDocumentApiResponse =
  /** status 201 Created */ TextDocumentDto;
export type UpdateTextDocumentApiArg = {
  updateTextDocumentRequest: UpdateTextDocumentRequest;
};
export type DeleteTextDocumentApiResponse = unknown;
export type DeleteTextDocumentApiArg = {
  id: string;
};
export type CreateUploadForTextDocumentApiResponse =
  /** status 200 OK */ UploadDto;
export type CreateUploadForTextDocumentApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  textDocumentId: string;
  uploadId: string;
  createUploadRequestDto: CreateUploadRequestDto;
};
export type DeleteUploadApiResponse = /** status 200 OK */ UploadDto;
export type DeleteUploadApiArg = {
  cookie?: string;
  organizationId?: string;
  personId?: string;
  uploadId: string;
};
export type CallingCodeDto = {
  countryAlpha3Code: string;
  countryName: string;
  value: string;
};
export type PhoneNumberDto = {
  callingCode: CallingCodeDto;
  number: string;
};
export type PersonDto = {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  email: string;
  corporateTitle?: string | null;
  phoneNumber?: PhoneNumberDto;
};
export type CountryDto = {
  id: string;
  alpha3Code: string;
  name: string;
};
export type IndustryDto = {
  id: string;
  name: string;
};
export type SubscriptionDto = {
  hasBuyerAccess: boolean;
  hasContractAccess: boolean;
  hasSignatureAccess: boolean;
  hasBankIdAccess: boolean;
  hasProductAccess: boolean;
  hasProjectAccess: boolean;
  hasNegotiationAccess: boolean;
  hasAiAccess: boolean;
  hasDocumentTemplateAccess: boolean;
  hasIFRSAccess: boolean;
  hasOrganizationAssessmentAccess: boolean;
};
export type DepartmentDto = {
  id: string;
  name: string;
};
export type AdminOrgDto = {
  id: string;
  owner: PersonDto;
  name: string;
  country: CountryDto;
  industries: IndustryDto[];
  aboutUs?: string | null;
  specializations?: string | null;
  homePageUrl?: string | null;
  size?: OrganizationSize | null;
  subscription: SubscriptionDto;
  departments: DepartmentDto[];
  azureTenantId?: string | null;
  azureUsersDefaultRole: OrgUserInviteRole;
  canSeeDeliveredBidsBeforeDeadline: boolean;
};
export type AdminListOrganizationsResponse = {
  orgs: AdminOrgDto[];
  count: number;
};
export type CustomNumberFieldDto = {
  id: string;
  name: string;
  isInternal: boolean;
  value?: number | null;
};
export type CustomAmountFieldDto = {
  id: string;
  name: string;
  isInternal: boolean;
  amount?: number | null;
  currency?: string | null;
};
export type CustomDateFieldDto = {
  id: string;
  name: string;
  isInternal: boolean;
  date?: string | null;
};
export type CustomEmailFieldDto = {
  id: string;
  name: string;
  isInternal: boolean;
  email?: string | null;
};
export type CustomBooleanFieldDto = {
  id: string;
  name: string;
  isInternal: boolean;
  isChecked?: boolean | null;
};
export type CustomEnumFieldDto = {
  id: string;
  name: string;
  isInternal: boolean;
  options?: string[] | null;
  value?: string | null;
};
export type CustomMultiEnumFieldDto = {
  id: string;
  name: string;
  isInternal: boolean;
  options?: string[] | null;
  values?: string[] | null;
};
export type CustomPercentageFieldDto = {
  id: string;
  name: string;
  isInternal: boolean;
  value?: number | null;
};
export type CustomPersonFieldDto = {
  id: string;
  name: string;
  isInternal: boolean;
  person?: PersonDto;
};
export type CustomPhoneFieldDto = {
  id: string;
  name: string;
  isInternal: boolean;
  number?: string | null;
  callingCode?: string | null;
};
export type CustomRichTextFieldDto = {
  id: string;
  name: string;
  isInternal: boolean;
  text?: string | null;
};
export type CustomTagFieldDto = {
  id: string;
  name: string;
  isInternal: boolean;
  tag?: string | null;
};
export type CustomUrlFieldDto = {
  id: string;
  name: string;
  isInternal: boolean;
  url?: string | null;
};
export type CustomTextFieldDto = {
  id: string;
  name: string;
  isInternal: boolean;
  text?: string | null;
};
export type ContractDataFieldSectionDto = {
  id: string;
  name: string;
  fields: (
    | CustomNumberFieldDto
    | CustomAmountFieldDto
    | CustomDateFieldDto
    | CustomEmailFieldDto
    | CustomBooleanFieldDto
    | CustomEnumFieldDto
    | CustomMultiEnumFieldDto
    | CustomPercentageFieldDto
    | CustomPersonFieldDto
    | CustomPhoneFieldDto
    | CustomRichTextFieldDto
    | CustomTagFieldDto
    | CustomUrlFieldDto
    | CustomTextFieldDto
  )[];
};
export type LeadTimeDto = {
  unit: LeadTimeUnit;
  amount: number;
};
export type ContractNotificationReceiversDto = {
  persons: PersonDto[];
  organizationRoles: OrganizationRoleType[];
  contractRoles: ContractRole[];
};
export type ContractDateReminderDto = {
  leadTime: LeadTimeDto;
  receivers: ContractNotificationReceiversDto;
};
export type ReminderIntervalDto = {
  unit: IntervalUnit;
  amount: number;
};
export type RecurringContractReminderDto = {
  interval: ReminderIntervalDto;
  receivers: ContractNotificationReceiversDto;
};
export type ContractNotificationSettingsDto = {
  startDateNotifications: ContractDateReminderDto[];
  endDateNotifications: ContractDateReminderDto[];
  warrantyExpirationNotifications: ContractDateReminderDto[];
  renewalDateNotifications: ContractDateReminderDto[];
  terminationDateNotifications: ContractDateReminderDto[];
  noticePeriodNotifications: ContractDateReminderDto[];
  optionExecutionDeadlineNotifications: ContractDateReminderDto[];
  optionStartDateNotifications: ContractDateReminderDto[];
  noEndDateNotifications: RecurringContractReminderDto[];
};
export type ContractTaskNotificationReceiversDto = {
  contractTaskRoles: ContractTaskRole[];
};
export type ContractTaskDateReminderDto = {
  leadTime: LeadTimeDto;
  receivers: ContractTaskNotificationReceiversDto;
};
export type ContractTaskNotificationSettingsDto = {
  dueDateNotifications: ContractTaskDateReminderDto[];
};
export type SourcingEventNotificationReceiversDto = {
  persons: PersonDto[];
  organizationRoles: OrganizationRoleType[];
  sourcingEventRoles: SourcingEventRole[];
};
export type SourcingEventDateReminderDto = {
  leadTime: LeadTimeDto;
  receivers: SourcingEventNotificationReceiversDto;
};
export type SourcingEventNotificationSettingsDto = {
  bidDeliveryDeadlineNotifications: SourcingEventDateReminderDto[];
};
export type DefaultNotificationSettingsDto = {
  contractNotificationSettings: ContractNotificationSettingsDto;
  contractTaskNotificationSettings: ContractTaskNotificationSettingsDto;
  sourcingEventNotificationSettings: SourcingEventNotificationSettingsDto;
};
export type MinimalOrgDto = {
  id: string;
  name: string;
  organizationNumber?: string | null;
  type: OrganizationType;
  country: CountryDto;
};
export type ScimGroupDto = {
  id: string;
  displayName: string;
  memberCount: number;
};
export type OrgDto = {
  id: string;
  owner: PersonDto;
  name: string;
  country: CountryDto;
  organizationNumber?: string | null;
  industries: IndustryDto[];
  aboutUs?: string | null;
  specializations?: string | null;
  homePageUrl?: string | null;
  size?: OrganizationSize | null;
  subscription: SubscriptionDto;
  departments: DepartmentDto[];
  azureTenantId?: string | null;
  allowScimUsersOnly?: boolean | null;
  enforceEntraIDAuthentication?: boolean | null;
  azureUsersDefaultRole: OrgUserInviteRole;
  canEdit: boolean;
  canLinkOrganizationEntry: boolean;
  canManageSupplierInvites: boolean;
  canManageUsers: boolean;
  canCreateEvent: boolean;
  canCreateContract: boolean;
  canCreateProject: boolean;
  hasProductAccess: boolean;
  hasProjectAccess: boolean;
  hasAiAccess: boolean;
  hasDocumentTemplateAccess: boolean;
  hasIFRSAccess: boolean;
  roles: OrganizationRoleType[];
  canOnlySeeCreatedEvents: boolean;
  canOnlySeeCreatedContracts: boolean;
  canSeeDeliveredBidsBeforeDeadline: boolean;
  customContractSections?: {
    [key: string]: {
      id_1?: ContractDataFieldSectionDto;
      id_2?: ContractDataFieldSectionDto;
    };
  };
  defaultNotificationSettings?: DefaultNotificationSettingsDto;
  internalParties?: MinimalOrgDto[] | null;
  scimGroups?: ScimGroupDto[] | null;
  contactPersons?: PersonDto[] | null;
};
export type AdminUpdateOrganizationRequest = {
  name?: string | null;
  hasContractAccess?: boolean | null;
  hasBuyerAccess?: boolean | null;
  hasSignatureAccess?: boolean | null;
  hasBankIdAccess?: boolean | null;
  hasProductAccess?: boolean | null;
  hasProjectAccess?: boolean | null;
  hasAiAccess?: boolean | null;
  hasDocumentTemplateAccess?: boolean | null;
  hasIFRSAccess?: boolean | null;
  hasOrganizationAssessmentAccess?: boolean | null;
  hasNegotiationAccess?: boolean | null;
  canSeeDeliveredBidsBeforeDeadline?: boolean | null;
};
export type GptMessage = {
  role: GPTRole;
  content: string;
};
export type AiChatResponse = {
  messages: GptMessage[];
};
export type JsonSchemaDto = {
  name: string;
  schema: string;
};
export type AiChatRequest = {
  model: AiModelDto;
  messages: GptMessage[];
  reasoningEffort: string;
  responseFormat?: JsonSchemaDto;
};
export type ScoreRangeDto = {
  minValue: number;
  maxValue?: number | null;
  description?: string | null;
};
export type AssessmentCriterionDto = {
  id: string;
  name: string;
  description?: string | null;
  scoreRange: ScoreRangeDto;
  order: number;
};
export type AssessmentDimensionDto = {
  id: string;
  name: string;
  description?: string | null;
  weight: number;
  criteria: AssessmentCriterionDto[];
  order: number;
};
export type AssessmentTemplateDto = {
  id: string;
  templateId: string;
  name: string;
  description?: string | null;
  owner: string;
  dimensions: AssessmentDimensionDto[];
  isActive: boolean;
  templateVersion: number;
  createdAt: string;
  modifiedAt: string;
};
export type CreateAssessmentDimensionDto = {
  name: string;
  description?: string | null;
  weight: number;
  criteria: any[];
  order: number;
};
export type CreateAssessmentTemplateRequestDto = {
  name: string;
  description?: string | null;
  dimensions: CreateAssessmentDimensionDto[];
  templateFamilyId?: string | null;
};
export type AssessedEntityDto = {
  organizationId?: string | null;
  organizationEntryId?: string | null;
};
export type CriterionScoreDto = {
  criterionId: string;
  criterionName: string;
  score?: number | null;
  scoreRange: ScoreRangeDto;
  comment?: string | null;
  lastUpdated: string;
};
export type DimensionScoreDto = {
  dimensionId: string;
  dimensionName: string;
  criterionScores: CriterionScoreDto[];
  directScore?: number | null;
  scoreRange?: ScoreRangeDto;
  comment?: string | null;
  lastUpdated: string;
};
export type AssessmentDto = {
  id: string;
  name: string;
  description?: string | null;
  assessedEntity: AssessedEntityDto;
  owner: MinimalOrgDto;
  template: AssessmentTemplateDto;
  assessor: PersonDto;
  dimensionScores: DimensionScoreDto[];
  status: string;
  completedAt?: string | null;
  createdAt: string;
  modifiedAt: string;
};
export type CreateCriterionScoreDto = {
  criterionId: string;
  score?: number | null;
  comment?: string | null;
};
export type CreateDimensionScoreDto = {
  dimensionId: string;
  directScore?: number | null;
  scoreRange?: ScoreRangeDto;
  comment?: string | null;
  criterionScores?: CreateCriterionScoreDto[] | null;
};
export type CreateAssessmentRequestDto = {
  name: string;
  description?: string | null;
  assessedEntity: AssessedEntityDto;
  templateId: string;
  dimensionScores: CreateDimensionScoreDto[];
};
export type ContractDataFieldDto = {
  type: ContractDataFieldType;
  name: ContractDataFieldNameDto;
};
export type BaseDataDto = {
  callingCodes: CallingCodeDto[];
  countries: CountryDto[];
  contractDataFields: ContractDataFieldDto[];
};
export type DocumentAddedClientEvent = {
  documentId: string;
};
export type ApiErrorDto = {
  errorCode: string;
};
export type AllTypes = {
  documentAddedClientEvent: DocumentAddedClientEvent;
  apiError: ApiErrorDto;
  apiErrorCodes: string[];
};
export type OrgPitchDto = {
  id: string;
  owner: PersonDto;
  name: string;
  country: CountryDto;
  aboutUs?: string | null;
  specializations?: string | null;
  homePageUrl?: string | null;
  size?: OrganizationSize | null;
};
export type OrganizationEmailInviteDto = {
  id: string;
  email: string;
  language: EmailInviteLanguage;
  status: EmailInviteStatusDto;
};
export type OrganizationInviteDto = {
  id: string;
  organizationId: string;
  organizationName: string;
  persons: PersonDto[];
  status: OrganizationInviteStatus;
  bidId?: string | null;
  emailInvites: OrganizationEmailInviteDto[];
  createdAt: string;
};
export type ContactPersonDto = {
  email: string;
  name: string;
  language: EmailInviteLanguage;
  title?: string | null;
  phoneNumber?: PhoneNumberDto;
};
export type LinkDto = {
  linkedBy: PersonDto;
  linkedAt: string;
  organizationId: string;
  organizationName: string;
};
export type OrganizationEntryDto = {
  id: string;
  name: string;
  country: CountryDto;
  organizationNumber: string;
  contactPersons?: ContactPersonDto[] | null;
  link?: LinkDto;
};
export type EmailInviteDto = {
  id: string;
  email: string;
  language: EmailInviteLanguage;
  lastReminderSentAt?: string | null;
  status: EmailInviteStatusDto;
  openedAt?: string | null;
  organizationEntry?: OrganizationEntryDto;
};
export type ViewDocumentAsOwnerDto = {
  id: string;
  name: string;
  mimeType: string;
  fileExtension: string;
  size: number;
  uploadedAt: string;
  uploadedBy: PersonDto;
  s3Key: string;
  fromUploadId: string;
  tags: string[];
};
export type UploadDto = {
  id: string;
  fileName: string;
  mimeType: string;
  fileExtension: string;
  url: string;
  renamedXAmzDate: string;
  renamedXAmzSignature: string;
  renamedXAmzAlgorithm: string;
  renamedXAmzCredential: string;
  policy: string;
  key: string;
};
export type DraftFieldsDto = {
  description?: string | null;
  deadline?: string | null;
  uploads: UploadDto[];
};
export type WinningProductBidsDto = {
  productId: string;
  bids: string[];
};
export type WinningProductGroupBidsDto = {
  productGroupId: string;
  bids: WinningProductBidsDto[];
};
export type PublishedFieldsDto = {
  description: string;
  deadline: string;
  uploads: UploadDto[];
  bidIds: string[];
  publishedAt: string;
  publishedBy: PersonDto;
  selectedProductGroupBids?: WinningProductGroupBidsDto[] | null;
};
export type EventValueDto = {
  value?: number | null;
  currency: Currency;
};
export type AwardedFieldsDto = {
  description: string;
  deadline: string;
  bidIds: string[];
  publishedAt: string;
  publishedBy: PersonDto;
  awardedAt: string;
  awardedBy: PersonDto;
  winningBidIds: string[];
  winningProductBids?: WinningProductGroupBidsDto[] | null;
  eventValue?: EventValueDto;
  messageToWinners?: string | null;
  messageToLosers?: string | null;
};
export type BseQuestionCommentDto = {
  id: string;
  createdAt: string;
  content: string;
  commenter: PersonDto;
};
export type BseQuestionDto = {
  id: string;
  status: BseQuestionStatus;
  askedByPerson: PersonDto;
  askedByOrgName: string;
  askedToOrganization: string;
  askedAt: string;
  subject: string;
  content: string;
  comments: BseQuestionCommentDto[];
  announcementId?: string | null;
};
export type BseAnnouncementDto = {
  id: string;
  createdByPerson: PersonDto;
  createdAt: string;
  title: string;
  content: string;
};
export type InternalMessageDto = {
  id: string;
  value: string;
  createdBy?: PersonDto;
  createdAt: string;
};
export type ParentProjectDto = {
  id: string;
  createdAt: string;
  modifiedAt: string;
  externalId?: string | null;
  name: string;
  startDate?: string | null;
  endDate?: string | null;
};
export type ChildProjectDto = {
  id: string;
  createdAt: string;
  modifiedAt: string;
  externalId?: string | null;
  name: string;
  startDate?: string | null;
  endDate?: string | null;
};
export type ProjectTypeDto = {
  id: string;
  name: ProjectTypeName;
};
export type ProjectDocumentDto = {
  id: string;
  name: string;
  mimeType: string;
  fileExtension: string;
  size: number;
  uploadedAt: string;
  uploadedBy: PersonDto;
  s3Key: string;
  fromUploadId: string;
  tags: string[];
};
export type ProjectDto = {
  id: string;
  createdAt: string;
  modifiedAt: string;
  status: ProjectStatus;
  externalId?: string | null;
  name: string;
  description?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  parent?: ParentProjectDto;
  children?: ChildProjectDto[] | null;
  projectResponsible?: PersonDto;
  sourcingResponsible?: PersonDto;
  projectType?: ProjectTypeDto;
  documents?: ProjectDocumentDto[] | null;
  canEdit: boolean;
  uploads?: UploadDto[] | null;
  descriptionDocuments?: ProjectDocumentDto[] | null;
};
export type NegotiationInviteDto = {
  id: string;
  organizationId: string;
  organizationName: string;
  persons: PersonDto[];
  message?: string | null;
  status: OrganizationInviteStatus;
  bidId?: string | null;
};
export type NegotiationRoundDto = {
  id: string;
  stateName: NegotiationStateName;
  status: NegotiationRoundStatusDto;
  roundNumber: number;
  deadline: string;
  invites: NegotiationInviteDto[];
  description?: string | null;
  documents: ViewDocumentAsOwnerDto[];
  descriptionDocuments: ViewDocumentAsOwnerDto[];
  uploads: UploadDto[];
  isFinalRound?: boolean | null;
  createdAt: string;
  modifiedAt: string;
  selectedProductBids?: WinningProductGroupBidsDto[] | null;
  questions: BseQuestionDto[];
};
export type NegotiationContextDto = {
  rounds: NegotiationRoundDto[];
};
export type BseDto = {
  id: string;
  status: BseStatusDto;
  stateName: BseStateNameDto;
  createdAt: string;
  modifiedAt: string;
  createdBy: PersonDto;
  owningOrganization: OrgPitchDto;
  organizationInvites: OrganizationInviteDto[];
  emailInvites: EmailInviteDto[];
  title: string;
  industries: IndustryDto[];
  documents: ViewDocumentAsOwnerDto[];
  descriptionDocuments: ViewDocumentAsOwnerDto[];
  timezone: string;
  referenceId?: string | null;
  draftFields?: DraftFieldsDto;
  publishedFields?: PublishedFieldsDto;
  awardedFields?: AwardedFieldsDto;
  questions?: BseQuestionDto[] | null;
  announcements: BseAnnouncementDto[];
  canEdit: boolean;
  internalConversation: InternalMessageDto[];
  projects?: ProjectDto[] | null;
  negotiationContext?: NegotiationContextDto;
};
export type CreateSourcingEventRequest = {
  title: string;
  timezone: string;
  projectId?: string | null;
  deadline?: string | null;
};
export type EditDescription = {
  value?: string | null;
};
export type EditDeadline = {
  value: string;
};
export type EditTimezone = {
  value: string;
};
export type EditReferenceId = {
  value?: string | null;
};
export type OrganizationEmailInviteInputDto = {
  email: string;
  language: EmailInviteLanguage;
};
export type OrganizationInviteInput = {
  organizationId: string;
  personIds: string[];
  emailInvites: OrganizationEmailInviteInputDto[];
};
export type AddOrganizationEntryEmailInviteRequest = {
  email: string;
  language: EmailInviteLanguage;
  organizationEntryId: string;
};
export type AddOrganizationEmailInviteRequest = {
  email: string;
  language: EmailInviteLanguage;
  organizationId: string;
};
export type EditEventValue = {
  value: EventValueDto;
};
export type EditSourcingEventRequest = {
  title?: string | null;
  description?: EditDescription;
  deadline?: EditDeadline;
  timezone?: EditTimezone;
  industryIds?: string[] | null;
  editReferenceId?: EditReferenceId;
  removeDocument?: string | null;
  removeDescriptionDocument?: string | null;
  organizationToInviteInput?: OrganizationInviteInput;
  organizationInviteIdToRemove?: string | null;
  orgEntryEmailInvitesToAdd?: AddOrganizationEntryEmailInviteRequest[] | null;
  orgEmailInvitesToAdd?: AddOrganizationEmailInviteRequest[] | null;
  emailInviteIdToRemove?: string | null;
  eventValue?: EditEventValue;
  emailInviteSecretOverride?: string | null;
  selectProductBids?: WinningProductGroupBidsDto[] | null;
  removeProductBids?: WinningProductGroupBidsDto[] | null;
  addInternalMessage?: string | null;
  editProjects?: string[] | null;
};
export type AwardToBidRequest = {
  bidIds: string[];
  messageToWinners?: string | null;
  messageToLosers?: string | null;
  eventValue?: EventValueDto;
};
export type NumberFieldDto = {
  id: string;
  name: string;
  populatedBy: Populater;
  isRequired: boolean;
  value?: number | null;
};
export type TextFieldDto = {
  id: string;
  name: string;
  populatedBy: Populater;
  isRequired: boolean;
  value?: string | null;
};
export type BooleanFieldDto = {
  id: string;
  name: string;
  populatedBy: Populater;
  isRequired: boolean;
  value?: boolean | null;
};
export type ProductBidDto = {
  productId: string;
  unitPrice: NumberFieldDto;
  productFields: (TextFieldDto | NumberFieldDto | BooleanFieldDto)[];
};
export type ProductGroupBidDto = {
  productGroupId: string;
  productBids: ProductBidDto[];
};
export type ViewSharedDocumentDto = {
  id: string;
  name: string;
  mimeType: string;
  fileExtension: string;
  size: number;
  uploadedAt: string;
  fromUploadId: string;
};
export type ReceivedBseBidDto = {
  id: string;
  owningOrganization: OrgPitchDto;
  forSourcingEventId: string;
  forNegotiationRound?: string | null;
  comment: string;
  products?: ProductGroupBidDto[] | null;
  documents: ViewSharedDocumentDto[];
  commentDocuments: ViewSharedDocumentDto[];
  uniqueForViewDeliveredBasicBidAsBuyerDto: boolean;
  contractId?: string | null;
};
export type ListReceivedBidsResponse = {
  bids: ReceivedBseBidDto[];
};
export type CancelSourcingEventRequest = {
  reason?: string | null;
};
export type CloseSourcingEventRequest = {
  reason?: string | null;
};
export type CreateUploadRequestDto = {
  fileName: string;
  fileSize: number;
  fileExtension: string;
  mimeType: string;
};
export type CreateNegotiationRoundRequest = {
  deadline: string;
  isFinalRound?: boolean | null;
};
export type CreateBseQuestionRequest = {
  subject: string;
  content: string;
  announcementId?: string | null;
  askedToOrganizationId?: string | null;
  negotiationRoundId?: string | null;
};
export type UpdateBseQuestionRequest = {
  status: BseQuestionStatus;
  negotiationRoundId?: string | null;
};
export type CreateBseQuestionCommentRequest = {
  content: string;
  negotiationRoundId?: string | null;
};
export type EvaluateSingleBidWithAiRequest = {
  bidId: string;
  prompt: string;
  jsonSchema: JsonSchemaDto;
};
export type EvaluateBidsWithAiRequest = {
  sourcingEventId: string;
  prompt: string;
  jsonSchema: JsonSchemaDto;
  negotiationRoundId?: string | null;
};
export type GenerateRequirementsWithAiRequest = {
  sourcingEventId: string;
  prompt: string;
  jsonSchema: JsonSchemaDto;
};
export type InviteInput = {
  organizationId: string;
  personIds: string[];
  message?: string | null;
};
export type SetInviteMessage = {
  inviteId: string;
  message: string;
};
export type EditNegotiationRequest = {
  title?: string | null;
  description?: EditDescription;
  deadline?: EditDeadline;
  removeDocument?: string | null;
  removeDescriptionDocument?: string | null;
  inviteInput?: InviteInput;
  setInviteMessage?: SetInviteMessage;
  inviteIdToRemove?: string | null;
  selectProductBids?: WinningProductGroupBidsDto[] | null;
  removeProductBids?: WinningProductGroupBidsDto[] | null;
};
export type ProductTemplateFieldDto = {
  id: string;
  name: string;
  type: ProductFieldType;
  populatedBy: Populater;
  isRequired: boolean;
};
export type ProductCommentDto = {
  comment: string;
  createdBy?: PersonDto;
  createdAt: string;
  modifiedAt: string;
};
export type ProductDto = {
  id: string;
  quantity: NumberFieldDto;
  productFields: (TextFieldDto | BooleanFieldDto | NumberFieldDto)[];
  conversation?: ProductCommentDto[] | null;
};
export type ProductGroupDto = {
  id: string;
  sourcingEventId?: string | null;
  projectId?: string | null;
  name: string;
  originProductGroupId?: string | null;
  productFields: ProductTemplateFieldDto[];
  products: ProductDto[];
};
export type ProductGroupsDto = {
  productGroups: ProductGroupDto[];
};
export type SeDto = {
  id: string;
  title: string;
  status: BseStatusDto;
  negotiationRound?: number | null;
  createdAt: string;
  modifiedAt: string;
  createdBy: PersonDto;
  deadline?: string | null;
  winners?: string[] | null;
  unansweredQuestionsCount: number;
  referenceId?: string | null;
  projects?: ProjectDto[] | null;
  canEdit: boolean;
  canView: boolean;
};
export type ListSeDtosResponse = {
  events: SeDto[];
  count: number;
};
export type ProjectDetailDto = {
  projectId: string;
  projectName: string;
  externalId?: string | null;
};
export type LinkedContract = {
  id: string;
  title?: string | null;
};
export type SupplierInfoDto = {
  isClimateCommitted: boolean;
  hasSignedCodeOfConduct: boolean;
  hasCustomerContact: boolean;
};
export type InternalPartyDto = {
  organizationId: string;
  organizationName: string;
};
export type OptionDto = {
  id: string;
  name: string;
  description: string;
  startDate?: string | null;
  deadline?: string | null;
  endDate?: string | null;
  duration?: number | null;
  isExecuted: boolean;
};
export type ContractReportDto = {
  id: string;
  createdAt?: string | null;
  modifiedAt?: string | null;
  version: number;
  contractId: string;
  title: string;
  description?: string | null;
  templateId?: string | null;
  templateName?: string | null;
  timezone?: string | null;
  currency?: string | null;
  owningOrganizationId?: string | null;
  owningOrganizationName?: string | null;
  createdById?: string | null;
  createdByEmail?: string | null;
  createdByName?: string | null;
  internalOwnerId?: string | null;
  internalOwnerEmail?: string | null;
  internalOwnerName?: string | null;
  counterpartyId?: string | null;
  counterpartyName?: string | null;
  counterpartyType?: string | null;
  state: ContractStateDto;
  status?: ContractStatusDto | null;
  counterpartyContactFullName?: string | null;
  counterpartyContactEmail?: string | null;
  counterpartyContactPhoneNumber?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  hasNoEndDate?: boolean | null;
  totalPrice?: number | null;
  estimatedValue?: number | null;
  priority?: string | null;
  risk?: string | null;
  renewalDate?: string | null;
  referenceNumber?: string | null;
  originSourcingEventId?: string | null;
  originSourcingEventName?: string | null;
  publishedById?: string | null;
  publishedByName?: string | null;
  publishedAt?: string | null;
  noticePeriod?: number | null;
  documentIds?: string[] | null;
  signedDocumentIds?: string[] | null;
  industryIds?: string[] | null;
  projectIds?: string[] | null;
  projectDetails?: ProjectDetailDto[] | null;
  terminationDate?: string | null;
  terminatedById?: string | null;
  terminatedByName?: string | null;
  terminationReason?: string | null;
  externalLink?: string | null;
  linkedContracts?: LinkedContract[] | null;
  paymentTermsInDays?: number | null;
  pricePerMonth?: number | null;
  pricePerYear?: number | null;
  purchasingPolicy?: string | null;
  supplierInfo?: SupplierInfoDto;
  internalParties?: InternalPartyDto[] | null;
  totalLiability?: number | null;
  warrantyExpirationDate?: string | null;
  supplierCategory?: string | null;
  options?: OptionDto[] | null;
  customFields?: {
    [key: string]: {
      example_id?: ContractDataFieldSectionDto;
    };
  };
};
export type ListContractReportsResponseDto = {
  reports: ContractReportDto[];
  count: number;
};
export type ContractReportFilterOptionDto = {
  value: string;
  label: string;
};
export type ListContractReportOptionsResponseDto = {
  options: ContractReportFilterOptionDto[];
  count: number;
};
export type OrganizationEntryPitchDto = {
  id: string;
  name: string;
  contactPersons?: ContactPersonDto[] | null;
};
export type OrgPublicProfileDto = {
  id: string;
  owner: PersonDto;
  name: string;
  country: CountryDto;
  organizationNumber?: string | null;
  industries: IndustryDto[];
  aboutUs?: string | null;
  specializations?: string | null;
  homePageUrl?: string | null;
  size?: OrganizationSize | null;
  contactPersons?: PersonDto[] | null;
  linkedEntries: OrganizationEntryPitchDto[];
};
export type CounterpartyDto = {
  organization?: OrgPublicProfileDto;
  organizationEntry?: OrganizationEntryDto;
};
export type ContractTemplateMinDto = {
  id: string;
  name?: string | null;
  commonType?: CommonTemplateType | null;
};
export type ContractEditingMetaFieldsDto = {
  uploads: UploadDto[];
  descriptionUploads: UploadDto[];
};
export type CustomFieldIdentifierDto = {
  sectionId: string;
  fieldId: string;
};
export type CounterpartyContactPersonDto = {
  fullName?: string | null;
  email?: string | null;
  phoneNumber?: PhoneNumberDto;
  person?: PersonDto;
};
export type ContractEndDateDto = {
  date?: string | null;
  hasNoEndDate: boolean;
};
export type LinkedContractDto = {
  id: string;
  title: string;
};
export type OriginSourcingEventDto = {
  id: string;
  title: string;
};
export type TerminationDateDto = {
  date: string;
  reason: string;
};
export type IfrsAdjustmentDto = {
  monthNumber: number;
  type: IfrsAdjustmentType;
  value: number;
};
export type IfrsValuesDto = {
  startDate?: string | null;
  startDateSecondPeriod?: string | null;
  monthlyPayment?: number | null;
  paymentIntervalInMonths?: number | null;
  contractLengthInMonths?: number | null;
  interestRate?: number | null;
  adjustments: IfrsAdjustmentDto[];
  filled: boolean;
};
export type InternalPartiesDto = {
  parties: InternalPartyDto[];
};
export type ContractDataFieldsDto = {
  missingDataFields: ContractDataFieldNameDto[];
  requiredDataFields: ContractDataFieldDto[];
  addedDataFields: ContractDataFieldDto[];
  customFields?: {
    [key: string]: {
      example_id?: ContractDataFieldSectionDto;
    };
  };
  missingCustomFields?: CustomFieldIdentifierDto[] | null;
  requiredCustomFields?: CustomFieldIdentifierDto[] | null;
  awardDate?: string | null;
  renewalDate?: string | null;
  counterpartyContactPerson?: CounterpartyContactPersonDto;
  deliveryTerms?: string | null;
  estimatedValue?: number | null;
  endDate?: ContractEndDateDto;
  externalLink?: string | null;
  industries?: IndustryDto[] | null;
  internalReferenceNumber?: string | null;
  linkedContracts?: LinkedContractDto[] | null;
  originSourcingEvent?: OriginSourcingEventDto;
  paymentTermsInDays?: number | null;
  pricePerMonth?: number | null;
  pricePerYear?: number | null;
  priority?: ContractPriority | null;
  purchasingPolicy?: string | null;
  risk?: ContractRisk | null;
  startDate?: string | null;
  totalLiability?: number | null;
  totalPrice?: number | null;
  warrantyExpirationDate?: string | null;
  terminationDate?: TerminationDateDto;
  ifrsValues?: IfrsValuesDto;
  internalParties?: InternalPartiesDto;
  supplierInfo?: SupplierInfoDto;
  supplierCategory?: Category | null;
  noticePeriod?: number | null;
  options?: OptionDto[] | null;
  projects?: ProjectDto[] | null;
};
export type DocumentSignerDto = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  status: DocumentSignerStatusDto;
  statusTimestamp?: string | null;
};
export type SignedAttachmentDto = {
  id: string;
  title?: string | null;
  description?: string | null;
  fileName?: string | null;
};
export type MergedDocumentDto = {
  id: string;
  name: string;
};
export type MergedTextDocumentDto = {
  id: string;
  name: string;
};
export type MergedDocumentsDto = {
  mainDocument?: MergedDocumentDto;
  mainTextDocument?: MergedTextDocumentDto;
  documents: MergedDocumentDto[];
  textDocuments: MergedTextDocumentDto[];
};
export type SignedDocumentDto = {
  id: string;
  name: string;
  uploadedAt: string;
  sentForSigningBy: PersonDto;
  status: SignedDocumentStatus;
  signers: DocumentSignerDto[];
  signicatDocumentId: string;
  signingCompleted: boolean;
  deadline?: string | null;
  attachments?: SignedAttachmentDto[] | null;
  mergedDocuments: MergedDocumentsDto;
  tags: string[];
};
export type ContractDocumentFieldsDto = {
  documents: ViewDocumentAsOwnerDto[];
  signedDocuments: SignedDocumentDto[];
  descriptionDocuments: ViewDocumentAsOwnerDto[];
};
export type NotificationInAdvanceConfigDto = {
  durationType: ContractNotificationDurationType;
  amount: number;
};
export type ContractNotificationDto = {
  id: string;
  type: ContractNotificationTypeDto;
  inAdvanceConfig?: NotificationInAdvanceConfigDto;
  receivers: PersonDto[];
};
export type ContractNotificationFieldsDto = {
  notifications: ContractNotificationDto[];
};
export type ContractDto = {
  id: string;
  owningOrganizationId: string;
  createdAt: string;
  modifiedAt: string;
  createdBy: PersonDto;
  state: ContractStateDto;
  status?: ContractStatusDto | null;
  title: string;
  description?: string | null;
  timezone: string;
  currency: Currency;
  counterparty?: CounterpartyDto;
  internalOwner: PersonDto;
  template?: ContractTemplateMinDto;
  editingMetaFields?: ContractEditingMetaFieldsDto;
  dataFields?: ContractDataFieldsDto;
  documentFields?: ContractDocumentFieldsDto;
  notificationFields?: ContractNotificationFieldsDto;
  canEditEverything: boolean;
  canViewDataFields: boolean;
  canViewDocuments: boolean;
  canViewNotifications: boolean;
  canViewTasks: boolean;
  canCreateTasks: boolean;
  canViewParticipants: boolean;
  canUpdateParticipants: boolean;
  owningOrgHasSignatureAccess: boolean;
};
export type CreateContractFromSourcingEventRequest = {
  eventId: string;
  bidId: string;
  title?: string | null;
  templateId?: string | null;
};
export type DeleteCustomFieldDto = {
  sectionId: string;
  fieldId: string;
};
export type UpsertCustomContractFieldDto = {
  field: CustomNumberFieldDto;
  sectionId: string;
};
export type CustomFieldSearchConfigDto = {
  fieldId: string;
  searchTerm: string;
};
export type CustomFieldSelectConfigDto = {
  fieldId: string;
  selected: boolean;
};
export type ContractReportQueryDto = {
  titleFilter?: string | null;
  templateIds?: string[] | null;
  templateNameSearchTerm?: string | null;
  owningOrganizationId: string;
  owningOrganizationIds?: string[] | null;
  owningOrganizationName?: string | null;
  projectIds?: string[] | null;
  projectSearchTerm?: string | null;
  internalPartyIds?: string[] | null;
  internalPartySearchTerm?: string | null;
  visibleCustomFields?: {
    [key: string]: {
      id_1?: ContractDataFieldSectionDto;
      id_2?: ContractDataFieldSectionDto;
    };
  };
  startDateFrom?: string | null;
  startDateTo?: string | null;
  endDateFrom?: string | null;
  endDateTo?: string | null;
  hasNoEndDate?: boolean | null;
  createdAtFrom?: string | null;
  createdAtTo?: string | null;
  modifiedAtFrom?: string | null;
  modifiedAtTo?: string | null;
  publishedAtFrom?: string | null;
  publishedAtTo?: string | null;
  renewalDateFrom?: string | null;
  renewalDateTo?: string | null;
  terminationDateFrom?: string | null;
  terminationDateTo?: string | null;
  warrantyExpirationDateFrom?: string | null;
  warrantyExpirationDateTo?: string | null;
  internalOwnerIds?: string[] | null;
  internalOwnerSearchTerm?: string | null;
  counterpartyIds?: string[] | null;
  counterpartyName?: string | null;
  counterpartyContactFullName?: string | null;
  counterpartyContactFullNames?: string[] | null;
  counterpartyContactEmail?: string | null;
  counterpartyContactEmails?: string[] | null;
  counterpartyContactPhoneNumber?: string | null;
  counterpartyContactPhoneNumbers?: string[] | null;
  statuses?: (ContractStatusDto | null)[] | null;
  state?: string | null;
  originSourcingEventIds?: string[] | null;
  originSourcingEventName?: string | null;
  riskLevels?: (ContractRisk | null)[] | null;
  priorityLevels?: (ContractPriority | null)[] | null;
  supplierCategories?: (Category | null)[] | null;
  referenceNumberSearchTerm?: string | null;
  referenceNumbers?: string[] | null;
  descriptionSearchTerm?: string | null;
  currencies?: string[] | null;
  publishedByIds?: string[] | null;
  publishedBySearchTerm?: string | null;
  terminatedByIds?: string[] | null;
  terminatedBySearchTerm?: string | null;
  terminationReasons?: string[] | null;
  linkedContractIds?: string[] | null;
  linkedContractTitleSearchTerm?: string | null;
  noticePeriodFrom?: number | null;
  noticePeriodTo?: number | null;
  paymentTermsInDaysFrom?: number | null;
  paymentTermsInDaysTo?: number | null;
  pricePerMonthFrom?: number | null;
  pricePerMonthTo?: number | null;
  pricePerYearFrom?: number | null;
  pricePerYearTo?: number | null;
  totalLiabilityFrom?: number | null;
  totalLiabilityTo?: number | null;
  externalLinkSearchTerm?: string | null;
  externalLinks?: string[] | null;
  purchasingPolicySearchTerm?: string | null;
  purchasingPolicies?: string[] | null;
  documentIds?: string[] | null;
  signedDocumentIds?: string[] | null;
  industryIds?: string[] | null;
  isClimateCommitted?: boolean | null;
  hasSignedCodeOfConduct?: boolean | null;
  hasCustomerContact?: boolean | null;
  customFieldSearch?: CustomFieldSearchConfigDto[] | null;
  customFieldSelect?: CustomFieldSelectConfigDto[] | null;
  optionNameSearchTerm?: string | null;
  optionNames?: string[] | null;
  limit?: number | null;
  skip?: number | null;
};
export type ContractTaskCommentDto = {
  id: string;
  text?: string | null;
  createdBy: PersonDto;
  createdAt: string;
  modifiedAt: string;
  deletedAt?: string | null;
  canUpdate: boolean;
};
export type UpdateContractTaskCommentRequest = {
  text: string;
};
export type ResponsibleOrganizationDto = {
  id: string;
  name: string;
};
export type ContractTaskDto = {
  id: string;
  status: ContractTaskStatus | null;
  displayStatus: ContractTaskStatusDto;
  owningOrganizationName: string;
  owningOrganizationId: string;
  title: string;
  description: string;
  dueDate: string;
  responsibleOrganization: ResponsibleOrganizationDto;
  responsiblePerson: PersonDto;
  responsibleParty: ContractTaskResponsiblePartyDto;
  contractId: string;
  contractTitle: string;
  canEdit: boolean;
  canDelete: boolean;
};
export type UpdateTaskRequest = {
  title?: string | null;
  description?: string | null;
  dueDate?: string | null;
  status?: ContractTaskStatus | null;
  responsiblePerson?: string | null;
};
export type ContractTaskChangeDto = {
  id: string;
  createdAt: string;
  changedByFullName: string;
  type: ContractTaskChangeType;
  oldValue: string;
  newValue: string;
};
export type ListChangesForContractTaskResponse = {
  changes: ContractTaskChangeDto[];
};
export type ListCommentsForContractTaskResponse = {
  comments: ContractTaskCommentDto[];
};
export type CreateContractTaskCommentRequest = {
  text: string;
};
export type TextDocumentDto = {
  id: string;
  createdAt: string;
  modifiedAt: string;
  owner: string;
  createdBy?: PersonDto;
  modifiedBy?: PersonDto;
  name: string;
  text: string;
  tags: string[];
  documents: ViewDocumentAsOwnerDto[];
};
export type CreateContractTextDocumentRequest = {
  name: string;
  text: string;
  tags: any[];
  contractId: string;
};
export type TextDocumentsDto = {
  documents: TextDocumentDto[];
};
export type ContractStatisticsDto = {
  numberOfContracts: number;
  numberOfActiveContracts: number;
  annualValueOfActiveContracts: number;
  numberOfContractsExpiringThisYear: number;
  annualValueOfContractsExpiringThisYear: number;
};
export type CancelSignedDocumentRequest = {
  documentId: string;
  reason: string;
};
export type DeleteContractParticipantRequest = {
  organizationId: string;
  personId?: string | null;
};
export type ContractOwningOrgDto = {
  id: string;
  name: string;
};
export type ContractOrgParticipantDto = {
  id: string;
  roleType: ContractRoleType;
  name: string;
};
export type ContractPersonParticipantDto = {
  id: string;
  roleType?: ContractRoleType;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  organizationId: string;
  organizationName: string;
};
export type ListParticipantsForContractResponse = {
  owningOrganization: ContractOwningOrgDto;
  organizationParticipants: ContractOrgParticipantDto[];
  personParticipants: ContractPersonParticipantDto[];
};
export type CreateContractParticipantRequest = {
  organizationId: string;
  personId?: string | null;
  roleType: ContractRoleType;
  shouldNotifyByEmail: boolean;
};
export type CreateContractTaskRequest = {
  title: string;
  description: string;
  dueDate: string;
  responsiblePersonId: string;
};
export type FilterOptionDto = {
  id: string;
  name: string;
};
export type ContractFilterOptionsDto = {
  counterparties: FilterOptionDto[];
  internalParties: FilterOptionDto[];
  persons: FilterOptionDto[];
  projects: ProjectDto[];
};
export type ContractListItemDto = {
  id: string;
  status?: ContractStatusDto | null;
  state: ContractStateDto;
  title: string;
  timezone: string;
  currency: Currency;
  counterparty?: CounterpartyDto;
  internalParties?: InternalPartyDto[] | null;
  endDate?: string | null;
  hasNoEndDate?: boolean | null;
  estimatedValue?: number | null;
  template?: ContractTemplateMinDto;
  owningOrganization: OrgPitchDto;
  internalOwner: PersonDto;
  containsIfrsValues: boolean;
  iamParticipant: boolean;
  projects?: ProjectDto[] | null;
};
export type ListContractsResponseDto = {
  contracts: ContractListItemDto[];
};
export type StartDocumentChatRequest = {
  documentId?: string | null;
  signedDocumentId?: string | null;
  model: AiModelDto;
  messages: GptMessage[];
  reasoningEffort: string;
  responseFormat?: JsonSchemaDto;
};
export type UpdateDocumentMetaRequest = {
  name?: string | null;
  tags?: string[] | null;
};
export type GetDownloadLinkResponse = {
  url: string;
};
export type CreateDocumentTemplateRequest = {
  name: string;
  text: string;
  tags: string[];
};
export type DownloadTextDocumentRequest = {
  fileName: string;
  content: string;
};
export type SearchIndustriesResponse = {
  industries: IndustryDto[];
};
export type SourcingEventNotificationMetaDto = {
  eventId: string;
  eventTitle: string;
  eventOwningOrganizationId: string;
  eventOwningOrganizationName: string;
};
export type BidNotificationMetaDto = {
  eventId: string;
  eventTitle: string;
  supplierOrganizationName: string;
};
export type SourcingEventQuestionNotificationMetaDto = {
  eventId: string;
  eventTitle: string;
  questionId: string;
  questionSubject: string;
  receiverRole: ReceiverRoleDto;
};
export type SourcingEventQuestionCommentNotificationMetaDto = {
  eventId: string;
  eventTitle: string;
  questionId: string;
  questionSubject: string;
  receiverRole: ReceiverRoleDto;
};
export type SourcingEventAnnouncementNotificationMetaDto = {
  eventId: string;
  eventTitle: string;
  announcementId: string;
  announcementTitle: string;
};
export type ContractNotificationMetaDto = {
  triggeredByFirstName?: string | null;
  contractName: string;
  contractId: string;
};
export type ContractReminderNotificationMetaDto = {
  contractName: string;
  contractId: string;
  contractTimezone: string;
  reminderType: ContractDateReminderType;
  date: string;
};
export type ContractTaskNotificationMetaDto = {
  triggeredByFirstName?: string | null;
  taskId: string;
  taskName: string;
  contractName: string;
  contractId: string;
};
export type ContractDocumentNotificationMetaDto = {
  documentId: string;
  documentName: string;
  contractName: string;
  contractId: string;
};
export type NotificationTypeMetaDto = {
  sourcingEvent?: SourcingEventNotificationMetaDto;
  negotiationRoundId?: string | null;
  bid?: BidNotificationMetaDto;
  sourcingEventQuestion?: SourcingEventQuestionNotificationMetaDto;
  sourcingEventQuestionComment?: SourcingEventQuestionCommentNotificationMetaDto;
  sourcingEventAnnouncement?: SourcingEventAnnouncementNotificationMetaDto;
  contract?: ContractNotificationMetaDto;
  contractReminder?: ContractReminderNotificationMetaDto;
  contractTask?: ContractTaskNotificationMetaDto;
  contractDocument?: ContractDocumentNotificationMetaDto;
};
export type NotificationDto = {
  id: string;
  createdAt: string;
  peeked: boolean;
  viewed: boolean;
  type: NotificationTypeDto;
  meta: NotificationTypeMetaDto;
};
export type ListNotificationsResponse = {
  notifications: NotificationDto[];
};
export type OrgUserDto = {
  person: PersonDto;
  roles: OrganizationRoleType[];
};
export type ListOrganizationUsersResponse = {
  users: OrgUserDto[];
  count: number;
};
export type ListOrganizationsForMeResponse = {
  orgs: OrgDto[];
};
export type EmailInviteContextDto = {
  emailInviteId: string;
  emailInviteType: EmailInviteTypeDto;
  secret: string;
};
export type CreateOrgRequest = {
  companyName: string;
  countryId: string;
  organizationNumber?: string | null;
  industryIds: string[];
  emailInviteContext?: EmailInviteContextDto;
};
export type MarkNotificationsRequest = {
  notificationIds: string[];
};
export type CreateNegotiationTextDocumentRequest = {
  name: string;
  text: string;
  tags: string[];
  negotiationRoundId: string;
};
export type AccessTokenDto = {
  accessToken: string;
  expiresIn: number;
  scope: string;
  tokenType: string;
};
export type GetAccessTokenRequest = {
  organizationId: string;
  personId: string;
};
export type SearchOrganizationResponse = {
  organization: OrgPitchDto;
};
export type CreateApiClientResponse = {
  clientId: string;
  secret: string;
};
export type CreateOrganizationEntryRequest = {
  name: string;
  industryIds: string[];
  countryId: string;
  organizationNumber: string;
};
export type UpdateOrganizationEntryRequest = {
  name?: string | null;
  countryId?: string | null;
  organizationNumber?: string | null;
  addContactPerson?: ContactPersonDto;
  editContactPerson?: ContactPersonDto;
  removeContactPersonEmail?: string | null;
};
export type LinkOrganizationEntryRequest = {
  organizationId: string;
};
export type AcceptedInfoDto = {
  acceptedAt: string;
  organizationName: string;
  personFirstName?: string | null;
  personLastName?: string | null;
  personEmail: string;
};
export type SupplierEmailInviteDto = {
  id: string;
  sentAt: string;
  sentBy: PersonDto;
  email: string;
  organizationName: string;
  personFirstName: string;
  personLastName: string;
  acceptedInfo?: AcceptedInfoDto;
};
export type OrgUserEmailInviteDto = {
  id: string;
  sentAt: string;
  sentBy: PersonDto;
  email: string;
  role: OrgUserInviteRole;
  organizationName: string;
};
export type AssessmentTemplatesDto = {
  templates: AssessmentTemplateDto[];
  count: number;
};
export type UpdateCriterionScoreDto = {
  criterionId: string;
  score?: number | null;
  comment?: string | null;
};
export type UpdateDimensionScoreDto = {
  dimensionId: string;
  directScore?: number | null;
  scoreRange?: ScoreRangeDto;
  comment?: string | null;
  criterionScores?: UpdateCriterionScoreDto[] | null;
};
export type UpdateAssessmentRequestDto = {
  name?: string | null;
  description?: string | null;
  dimensionScores?: UpdateDimensionScoreDto[] | null;
  status?: string | null;
};
export type AssessmentsDto = {
  assessments: AssessmentDto[];
  count: number;
};
export type EditName = {
  value: string;
};
export type EditCountry = {
  countryId: string;
};
export type EditAboutUs = {
  value?: string | null;
};
export type EditSpecializations = {
  value?: string | null;
};
export type EditHomePageUrl = {
  url?: string | null;
};
export type EditIndustries = {
  value: string[];
};
export type EditSize = {
  size?: OrganizationSize | null;
};
export type EditAdmins = {
  value: string[];
};
export type EditAzureUsersDefaultRole = {
  value: OrgUserInviteRole;
};
export type EditInternalParties = {
  parties?: string[] | null;
  selfRegisteredParties?: string[] | null;
};
export type EditContactPersons = {
  persons?: string[] | null;
};
export type EditOrganizationRequest = {
  name?: EditName;
  country?: EditCountry;
  organizationNumber?: string | null;
  aboutUs?: EditAboutUs;
  specializations?: EditSpecializations;
  homePageUrl?: EditHomePageUrl;
  industries?: EditIndustries;
  size?: EditSize;
  admins?: EditAdmins;
  azureUsersDefaultRole?: EditAzureUsersDefaultRole;
  allowScimUsersOnly?: boolean | null;
  enforceEntraIDAuthentication?: boolean | null;
  canOnlySeeCreatedContracts?: boolean | null;
  canOnlySeeCreatedEvents?: boolean | null;
  defaultNotificationSettings?: DefaultNotificationSettingsDto;
  internalParties?: EditInternalParties;
  contactPersons?: EditContactPersons;
};
export type ListOrganizationInvitesResponse = {
  invitations: SupplierEmailInviteDto[];
};
export type SupplierInviteParams = {
  personFirstName: string;
  personLastName: string;
  organizationName: string;
};
export type CreateEmailInviteRequest = {
  email: string;
  supplierInvite: SupplierInviteParams;
  language: EmailInviteLanguage;
};
export type UpdateOrgTenantRequest = {
  tenantId?: string | null;
};
export type ListOrgUserEmailInvitesResponse = {
  invitations: OrgUserEmailInviteDto[];
};
export type CreateOrgUserEmailInviteRequest = {
  email: string;
  role: OrgUserInviteRole;
  language: EmailInviteLanguage;
  emailInviteSecretOverride?: string | null;
};
export type UpdateOrgUserRoleRequest = {
  newRoles: OrganizationRoleType[];
};
export type ListContractTasksResponseDto = {
  tasks: ContractTaskDto[];
};
export type ContractTemplateDto = {
  id: string;
  name?: string | null;
  description?: string | null;
  commonType?: CommonTemplateType | null;
  ownerType: ContractOwnerContextType;
  createdAt: string;
  createdBy?: PersonDto;
  requiredFields: ContractDataFieldDto[];
  requiredCustomDataFields?: {
    [key: string]: {
      id?: ContractDataFieldSectionDto;
    };
  };
};
export type ListContractTemplatesResponseDto = {
  templates: ContractTemplateDto[];
};
export type UpsertContractTemplateRequest = {
  name: string;
  description?: string | null;
  requiredDataFields: ContractDataFieldNameDto[];
  requiredCustomDataFields: CustomFieldIdentifierDto[];
};
export type CounterpartyInputDto = {
  organizationId?: string | null;
  organizationEntryId?: string | null;
};
export type EditCounterparty = {
  counterparty: CounterpartyInputDto;
};
export type EditTemplate = {
  templateId?: string | null;
};
export type EditInternalOwner = {
  personId: string;
};
export type EditCounterpartyContactPerson = {
  fullName?: string | null;
  email?: string | null;
  phone?: PhoneNumberDto;
  personId?: string | null;
};
export type EditDeliveryTerms = {
  terms?: string | null;
};
export type EditPurchasingPolicy = {
  policy?: string | null;
};
export type EditOriginSourcingEvent = {
  eventId?: string | null;
};
export type EditIndustryIds = {
  industryIds: string[];
};
export type EditInternalReferenceNumber = {
  number?: string | null;
};
export type EditExternalLink = {
  link?: string | null;
};
export type EditRisk = {
  risk: ContractRisk | null;
};
export type EditPriority = {
  priority: ContractPriority | null;
};
export type EditLinkedContracts = {
  contractIds: string[];
};
export type EditPricePerYear = {
  amount?: number | null;
};
export type EditPricePerMonth = {
  amount?: number | null;
};
export type EditTotalPrice = {
  amount?: number | null;
};
export type EditTotalLiability = {
  amount?: number | null;
};
export type EditPaymentTermsInDays = {
  days?: number | null;
};
export type EditEndDate = {
  date?: string | null;
  hasNoEndDate: boolean;
};
export type EditStartDate = {
  date?: string | null;
};
export type EditAwardDate = {
  date?: string | null;
};
export type EditRenewalDate = {
  date?: string | null;
};
export type EditWarrantyExpirationDate = {
  date?: string | null;
};
export type EditValueAmount = {
  amount?: number | null;
};
export type EditIfrsValues = {
  startDate?: string | null;
  startDateSecondPeriod?: string | null;
  monthlyPayment?: number | null;
  paymentIntervalInMonths?: number | null;
  contractLengthInMonths?: number | null;
  interestRate?: number | null;
};
export type EditSupplierInfo = {
  isClimateCommitted: boolean;
  hasSignedCodeOfConduct: boolean;
  hasCustomerContact: boolean;
};
export type EditCurrency = {
  currency: Currency;
};
export type AddTerminationDate = {
  date: string;
  reason: string;
};
export type AddDateReminderNotification = {
  inAdvanceUnit: ContractNotificationDurationType;
  inAdvanceAmount: number;
  receiverIds: string[];
  dateReminderType: ContractDateReminderType;
};
export type AddAdjustment = {
  date: string;
  value: number;
};
export type EditSupplierCategory = {
  category?: Category | null;
};
export type EditNoticePeriod = {
  duration: number;
};
export type EditOption = {
  id: string;
  name: string;
  description: string;
  startDate?: string | null;
  deadline?: string | null;
  endDate?: string | null;
  duration?: number | null;
  isExecuted: boolean;
};
export type ExecuteOption = {
  id: string;
};
export type RemoveOption = {
  id: string;
};
export type UpdateContractRequest = {
  title?: string | null;
  description?: string | null;
  removeDocument?: string | null;
  removeDescriptionDocument?: string | null;
  counterparty?: EditCounterparty;
  template?: EditTemplate;
  editProjects?: string[] | null;
  internalOwner?: EditInternalOwner;
  counterpartyContactPerson?: EditCounterpartyContactPerson;
  deliveryTerms?: EditDeliveryTerms;
  purchasingPolicy?: EditPurchasingPolicy;
  originSourcingEvent?: EditOriginSourcingEvent;
  industry?: EditIndustryIds;
  internalReferenceNumber?: EditInternalReferenceNumber;
  externalLink?: EditExternalLink;
  risk?: EditRisk;
  priority?: EditPriority;
  linkedContracts?: EditLinkedContracts;
  pricePerYear?: EditPricePerYear;
  pricePerMonth?: EditPricePerMonth;
  totalPrice?: EditTotalPrice;
  totalLiability?: EditTotalLiability;
  paymentTermsInDays?: EditPaymentTermsInDays;
  endDate?: EditEndDate;
  startDate?: EditStartDate;
  awardDate?: EditAwardDate;
  renewalDate?: EditRenewalDate;
  warrantyExpirationDate?: EditWarrantyExpirationDate;
  timezone?: EditTimezone;
  valueAmount?: EditValueAmount;
  ifrsValues?: EditIfrsValues;
  supplierInfo?: EditSupplierInfo;
  internalParties?: EditInternalParties;
  currency?: EditCurrency;
  terminationDate?: AddTerminationDate;
  addDateReminderNotification?: AddDateReminderNotification;
  removeNotification?: string | null;
  addDataFields?: ContractDataFieldNameDto[] | null;
  editCustomFields?: {
    [key: string]: {
      id_1?: ContractDataFieldSectionDto;
      id_2?: ContractDataFieldSectionDto;
    };
  };
  removeDataField?: ContractDataFieldNameDto;
  addMonetaryValueAdjustment?: AddAdjustment;
  removeMonetaryValueAdjustment?: string | null;
  addPercentageValueAdjustment?: AddAdjustment;
  removePercentageValueAdjustment?: string | null;
  editSupplierCategory?: EditSupplierCategory;
  editNoticePeriod?: EditNoticePeriod;
  editOption?: EditOption;
  executeOption?: ExecuteOption;
  removeOption?: RemoveOption;
};
export type TextDocumentInputDto = {
  textDocumentId: string;
  htmlContent: string;
};
export type SignerDto = {
  firstName: string;
  lastName: string;
  email: string;
  signatureMethod: DocumentSignerMethod;
};
export type CreateSignedDocumentRequest = {
  mainDocumentId: string;
  attachments?: string[] | null;
  textDocumentAttachments?: TextDocumentInputDto[] | null;
  signers: SignerDto[];
  language: EmailInviteLanguage;
  deadline?: string | null;
};
export type CreateSignedTextDocumentRequest = {
  textDocumentId: string;
  htmlContent: string;
  attachments?: string[] | null;
  textDocumentAttachments?: TextDocumentInputDto[] | null;
  signers: SignerDto[];
  language: EmailInviteLanguage;
  deadline?: string | null;
};
export type CreateContractRequest = {
  title: string;
  timezone: string;
  templateId?: string | null;
  projects?: string[] | null;
};
export type ListPersonsResponse = {
  persons: PersonDto[];
  count: number;
};
export type ListPersonsByIdsResponse = {
  persons: PersonDto[];
};
export type MeDto = {
  id: string;
  identityId?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email: string;
  emailVerified: boolean;
  corporateTitle?: string | null;
  phoneNumber?: PhoneNumberDto;
  canCreateOrg: boolean;
  intercomSecretHash: string;
};
export type EditPhoneNumber = {
  value?: PhoneNumberDto;
};
export type EditMeRequest = {
  firstName?: string | null;
  lastName?: string | null;
  corporateTitle?: string | null;
  phoneNumber?: EditPhoneNumber;
};
export type CreateProductDto = {
  id?: string | null;
  quantity: NumberFieldDto;
  productFields: (TextFieldDto | NumberFieldDto | BooleanFieldDto)[];
};
export type AddProductDto = {
  product: CreateProductDto;
};
export type AddProductCommentDto = {
  comment: string;
};
export type EditProductDto = {
  id: string;
  quantity?: NumberFieldDto;
  productFields?: (TextFieldDto | BooleanFieldDto | NumberFieldDto)[] | null;
  comment?: AddProductCommentDto;
};
export type DeleteProductDto = {
  productId: string;
};
export type EditProductGroupDto = {
  id: string;
  addProduct?: AddProductDto;
  editProduct?: EditProductDto;
  upsertProducts?: ProductDto[] | null;
  deleteProduct?: DeleteProductDto;
};
export type CreateProductGroupDto = {
  sourcingEventId?: string | null;
  projectId?: string | null;
  name: string;
  originProductGroupId?: string | null;
  productFields: ProductTemplateFieldDto[];
  products: CreateProductDto[];
};
export type ProductTemplateDto = {
  id: string;
  name: string;
  createdAt: string;
  modifiedAt: string;
  productFields: ProductTemplateFieldDto[];
};
export type ProductTemplatesDto = {
  templates: ProductTemplateDto[];
};
export type EditStringValue = {
  value?: string | null;
};
export type EditLocalDateValue = {
  value?: string | null;
};
export type UpdateProjectRequest = {
  id: string;
  externalId?: EditStringValue;
  name?: string | null;
  description?: EditStringValue;
  startDate?: EditLocalDateValue;
  endDate?: EditLocalDateValue;
  parent?: EditStringValue;
  projectResponsible?: EditStringValue;
  sourcingResponsible?: EditStringValue;
  projectType?: EditStringValue;
  documents?: string[] | null;
  removeDocument?: string | null;
  removeDescriptionDocument?: string | null;
};
export type CreateProjectRequest = {
  id?: string | null;
  externalId?: string | null;
  name: string;
  description?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  parent?: string | null;
  projectResponsible?: string | null;
  sourcingResponsible?: string | null;
  projectType?: string | null;
  documents?: string[] | null;
};
export type CreateProjectTextDocumentRequest = {
  name: string;
  text: string;
  tags: any[];
  projectId: string;
};
export type ProjectsDto = {
  projects: ProjectDto[];
};
export type ScimMember = {
  value: string;
  display?: string | null;
};
export type GroupDto = {
  id: string;
  displayName: string;
  schemas: string[];
  members?: ScimMember[] | null;
};
export type GroupsDto = {
  Resources: GroupDto[];
  totalResults: number;
  schemas: string[];
};
export type MetaDto = {
  resourceType: string;
  location?: string | null;
};
export type CreateGroupDto = {
  schemas: string[];
  externalId?: string | null;
  displayName: string;
  members?: ScimMember[] | null;
  meta?: MetaDto;
};
export type OperationValueObject = {
  userName?: string | null;
  givenName?: string | null;
  familyName?: string | null;
  title?: string | null;
  active?: boolean | null;
  displayName?: string | null;
  members?: ScimMember[] | null;
  roles?: string | null;
};
export type ObjectPatchOperation = {
  op: PatchOperationType;
  value: OperationValueObject;
};
export type StringPatchOperation = {
  op: PatchOperationType;
  path: string;
  value: string;
};
export type BooleanPatchOperation = {
  op: PatchOperationType;
  path: string;
  value: boolean;
};
export type ListPatchOperationValue = {
  value: string;
};
export type ListPatchOperation = {
  op: PatchOperationType;
  path: string;
  value: ListPatchOperationValue[];
};
export type UpdateGroupRequest = {
  schemas: string[];
  Operations: (
    | ObjectPatchOperation
    | StringPatchOperation
    | BooleanPatchOperation
    | ListPatchOperation
  )[];
};
export type SchemaSubAttributeDto = {
  name: string;
  type: SchemaAttributeType;
  description: string;
  required: boolean;
  caseExact: boolean;
  mutability: SchemaAttributeMutability;
  returned: string;
  uniqueness: SchemaAttributeUniqueness;
  referenceTypes?: ScimResourceType[] | null;
  canonicalValues?: ScimResourceType[] | null;
  multiValued: boolean;
};
export type SchemaAttributeDto = {
  name: string;
  type: SchemaAttributeType;
  multiValued: boolean;
  description: string;
  required: boolean;
  subAttributes?: SchemaSubAttributeDto[] | null;
  caseExact: boolean;
  mutability: SchemaAttributeMutability;
  returned: string;
  uniqueness: SchemaAttributeUniqueness;
};
export type SchemaDto = {
  schemas: string[];
  id: string;
  name: string;
  description: string;
  attributes: SchemaAttributeDto[];
  meta: MetaDto;
};
export type SchemasDto = {
  schemas: string[];
  itemsPerPage: number;
  startIndex: number;
  totalResults: number;
  Resources: SchemaDto[];
};
export type TokenResponse = {
  token: string;
};
export type NameDto = {
  givenName?: string | null;
  familyName?: string | null;
};
export type RoleResponse = {
  value: string;
};
export type UserDto = {
  id: string;
  userName: string;
  name: NameDto;
  title?: string | null;
  roles: RoleResponse[];
  schemas: string[];
  active: boolean;
};
export type UsersDto = {
  Resources: UserDto[];
  totalResults: number;
  schemas: string[];
};
export type RoleValueContent = {
  id?: string | null;
  value?: string | null;
  displayName?: string | null;
};
export type CreateUserDto = {
  userName: string;
  name?: NameDto;
  title?: string | null;
  roles?: RoleValueContent[] | null;
  active?: boolean | null;
};
export type SearchOrganizationsAndEntriesResponse = {
  organizations: MinimalOrgDto[];
  count: number;
};
export type UpdateSignedDocumentMetaRequest = {
  tags: string[];
};
export type CreateSourcingEventTextDocumentRequest = {
  name: string;
  text: string;
  tags: string[];
  sourcingEventId: string;
};
export type BseSupplierViewAwardedFieldsDto = {
  awardedAt: string;
  iamWinningDepartment: boolean;
  message?: string | null;
};
export type SupplierNegotiationRoundDto = {
  id: string;
  roundNumber: number;
  deadline: string;
  description?: string | null;
  documents: ViewSharedDocumentDto[];
  descriptionDocuments: ViewSharedDocumentDto[];
  isFinalRound?: boolean | null;
  createdAt: string;
  modifiedAt: string;
  bidId?: string | null;
  status: BseBidStatusDto;
  questions: BseQuestionDto[];
  canAskQuestions: boolean;
};
export type SourcingEventSupplierView = {
  id: string;
  status: BseBidStatusDto;
  stateName: BseStatusDto;
  publishedAt: string;
  modifiedAt: string;
  contactPerson: PersonDto;
  owningOrganization: OrgPublicProfileDto;
  title: string;
  documents: ViewSharedDocumentDto[];
  descriptionDocuments: ViewSharedDocumentDto[];
  description: string;
  deadline: string;
  createdBidId?: string | null;
  invitationAccepted: boolean;
  awardedFieldsDto?: BseSupplierViewAwardedFieldsDto;
  questions: BseQuestionDto[];
  announcements: BseAnnouncementDto[];
  canDeliverBid: boolean;
  hasRegisteredViewing: boolean;
  canAskQuestions: boolean;
  negotiationRounds?: SupplierNegotiationRoundDto[] | null;
};
export type BseBidDraftFieldsDto = {
  comment?: string | null;
  products?: ProductGroupBidDto[] | null;
};
export type BseBidDeliveredFieldsDto = {
  comment: string;
  products?: ProductGroupBidDto[] | null;
  deliveredAt: string;
  deliveredBy: PersonDto;
  winningBid?: boolean | null;
};
export type BidViewForSupplierDto = {
  id: string;
  status: BseBidStatusDto;
  owningOrganization: OrgPitchDto;
  createdAt: string;
  modifiedAt: string;
  createdBy: PersonDto;
  forEvent: SourcingEventSupplierView;
  forNegotiationRound?: string | null;
  documents: ViewDocumentAsOwnerDto[];
  commentDocuments: ViewDocumentAsOwnerDto[];
  uploads: UploadDto[];
  draftFields?: BseBidDraftFieldsDto;
  deliveredFields?: BseBidDeliveredFieldsDto;
  canEdit: boolean;
  uniqueForBseBidDto: boolean;
};
export type EditComment = {
  value?: string | null;
};
export type RemoveDocument = {
  value: string;
};
export type EditProducts = {
  products: ProductGroupBidDto[];
};
export type EditBidRequest = {
  comment?: EditComment;
  removeDocument?: RemoveDocument;
  editProducts?: EditProducts;
};
export type CreateBseAnnouncementRequest = {
  title: string;
  content: string;
};
export type GetProcurementProtocolRequest = {
  descriptionHtml: string;
};
export type CreateDraftBidRequest = {
  sourcingEventId: string;
  organizationId: string;
  negotiationRoundId?: string | null;
};
export type SourcingEventListItemForSupplier = {
  id: string;
  title: string;
  status: BseBidStatusDto;
  createdAt: string;
  modifiedAt: string;
  createdBy: PersonDto;
  deadline: string;
  owningOrganizationName: string;
  contactPerson: PersonDto;
  timezone: string;
  negotiationRound?: number | null;
  negotiationRoundId?: string | null;
};
export type BidListItemForSupplierDto = {
  id: string;
  status: BseBidStatusDto;
  owningOrganization: OrgPitchDto;
  createdAt: string;
  modifiedAt: string;
  createdBy: PersonDto;
  forEvent: SourcingEventListItemForSupplier;
  forNegotiationRound?: number | null;
  forNegotiationRoundId?: string | null;
  winningBid?: boolean | null;
  canEdit: boolean;
  uniqueForBidDto: boolean;
};
export type ListBidsResponse = {
  bids: BidListItemForSupplierDto[];
};
export type SeEmailInviteDto = {
  id: string;
  sentAt: string;
  sentByPersonFirstName?: string | null;
  sentByPersonLastName?: string | null;
  owningOrganizationName: string;
  eventTitle: string;
  eventDeadline: string;
};
export type DeliverBidRequest = {
  eventId: string;
  bidId: string;
  negotiationRoundId?: string | null;
};
export type ListSeSupplierViewsResponse = {
  uniqueForListSourcingEventsAsSupplierResponse: boolean;
  events: SourcingEventListItemForSupplier[];
};
export type SupplierProductDto = {
  id: string;
  quantity: NumberFieldDto;
  productFields: (TextFieldDto | BooleanFieldDto | NumberFieldDto)[];
};
export type SupplierProductGroupDto = {
  id: string;
  sourcingEventId: string;
  name: string;
  productFields: ProductTemplateFieldDto[];
  products: SupplierProductDto[];
};
export type SupplierProductGroupsDto = {
  productGroups: SupplierProductGroupDto[];
};
export type UpdateTextDocumentRequest = {
  id: string;
  name?: string | null;
  text?: string | null;
  tags?: string[] | null;
  removeEmbeddedDocument?: string | null;
};
export enum AdminEntityType {
  Bid = "Bid",
  Contract = "Contract",
  Document = "Document",
  EmailInvite = "EmailInvite",
  EmailOutboxItem = "EmailOutboxItem",
  Organization = "Organization",
  Person = "Person",
  SourcingEvent = "SourcingEvent",
  Upload = "Upload",
}
export enum OrganizationSize {
  OneToTen = "ONE_TO_TEN",
  TenToFifty = "TEN_TO_FIFTY",
  FiftyToTwoHundredAndFifty = "FIFTY_TO_TWO_HUNDRED_AND_FIFTY",
  TwoHundredAndFiftyToThousand = "TWO_HUNDRED_AND_FIFTY_TO_THOUSAND",
  ThousandToTenThousand = "THOUSAND_TO_TEN_THOUSAND",
  TenThousandPlus = "TEN_THOUSAND_PLUS",
}
export enum OrgUserInviteRole {
  Admin = "Admin",
  SourcingCreator = "SourcingCreator",
  SourcingViewer = "SourcingViewer",
  ContractCreator = "ContractCreator",
  ContractViewer = "ContractViewer",
  ProjectViewer = "ProjectViewer",
  ProjectCreator = "ProjectCreator",
}
export enum OrganizationRoleType {
  Owner = "Owner",
  Admin = "Admin",
  SourcingCreator = "SourcingCreator",
  SourcingViewer = "SourcingViewer",
  ContractCreator = "ContractCreator",
  ContractViewer = "ContractViewer",
  ProjectViewer = "ProjectViewer",
  ProjectCreator = "ProjectCreator",
}
export enum LeadTimeUnit {
  Days = "Days",
  Months = "Months",
}
export enum ContractRole {
  InternalOwner = "InternalOwner",
  CounterParty = "CounterParty",
  InvitedParty = "InvitedParty",
}
export enum IntervalUnit {
  Days = "Days",
  Months = "Months",
}
export enum ContractTaskRole {
  Responsible = "Responsible",
}
export enum SourcingEventRole {
  Publisher = "Publisher",
  InvitedSupplier = "InvitedSupplier",
}
export enum OrganizationType {
  Organization = "Organization",
  OrganizationEntry = "OrganizationEntry",
}
export enum GPTRole {
  System = "system",
  Assistant = "assistant",
  User = "user",
  Function = "function",
  Tool = "tool",
}
export enum AiModelDto {
  Gpt52 = "Gpt52",
  Gpt5 = "Gpt5",
  Gpt5Mini = "Gpt5Mini",
  Gpt5Nano = "Gpt5Nano",
}
export enum ContractDataFieldType {
  Date = "Date",
  Financial = "Financial",
  Commercial = "Commercial",
  TermsAndConditions = "TermsAndConditions",
  Other = "Other",
}
export enum ContractDataFieldNameDto {
  StartDate = "StartDate",
  EndDate = "EndDate",
  AwardDate = "AwardDate",
  WarrantyExpirationDate = "WarrantyExpirationDate",
  RenewalDate = "RenewalDate",
  CounterpartyContactPerson = "CounterpartyContactPerson",
  DeliveryTerms = "DeliveryTerms",
  EstimatedValue = "EstimatedValue",
  ExternalLink = "ExternalLink",
  InternalReferenceNumber = "InternalReferenceNumber",
  LinkedContracts = "LinkedContracts",
  OriginSourcingEvent = "OriginSourcingEvent",
  PaymentTermsInDays = "PaymentTermsInDays",
  PricePerMonth = "PricePerMonth",
  PricePerYear = "PricePerYear",
  Priority = "Priority",
  PurchasingPolicy = "PurchasingPolicy",
  Risk = "Risk",
  TotalLiability = "TotalLiability",
  TotalPrice = "TotalPrice",
  TerminationDate = "TerminationDate",
  IfrsValues = "IfrsValues",
  SupplierInfo = "SupplierInfo",
  InternalParties = "InternalParties",
  SupplierCategory = "SupplierCategory",
  NoticePeriod = "NoticePeriod",
  Options = "Options",
  Projects = "Projects",
}
export enum BseStatusDto {
  Draft = "Draft",
  Published = "Published",
  DeadlineHasPassed = "DeadlineHasPassed",
  Completed = "Completed",
  Awarded = "Awarded",
  Cancelled = "Cancelled",
  Closed = "Closed",
}
export enum BseStateNameDto {
  Draft = "Draft",
  Published = "Published",
  Completed = "Completed",
  Awarded = "Awarded",
  Cancelled = "Cancelled",
  Closed = "Closed",
}
export enum OrganizationInviteStatus {
  IsWinner = "IsWinner",
  IsLoser = "IsLoser",
  BidWasNotNotSubmitted = "BidWasNotNotSubmitted",
  BidIsSubmitted = "BidIsSubmitted",
  InvitationAccepted = "InvitationAccepted",
  DraftCreated = "DraftCreated",
  InvitationDeclined = "InvitationDeclined",
  HasSeenEvent = "HasSeenEvent",
  InvitationSent = "InvitationSent",
}
export enum EmailInviteLanguage {
  En = "En",
  No = "No",
}
export enum EmailInviteStatusDto {
  Sent = "Sent",
  Opened = "Opened",
  SendingFailed = "SendingFailed",
}
export enum Currency {
  Nok = "NOK",
  Usd = "USD",
  Eur = "EUR",
  Dkk = "DKK",
}
export enum BseQuestionStatus {
  Unresolved = "Unresolved",
  Resolved = "Resolved",
}
export enum ProjectStatus {
  NotStarted = "NotStarted",
  Ongoing = "Ongoing",
  Ended = "Ended",
}
export enum ProjectTypeName {
  BidCalculation = "BidCalculation",
  Other = "Other",
}
export enum NegotiationStateName {
  Draft = "Draft",
  Published = "Published",
  Completed = "Completed",
}
export enum NegotiationRoundStatusDto {
  Draft = "Draft",
  Published = "Published",
  DeadlineHasPassed = "DeadlineHasPassed",
  Completed = "Completed",
}
export enum Populater {
  Buyer = "Buyer",
  Supplier = "Supplier",
}
export enum ProductFieldType {
  Text = "Text",
  Number = "Number",
  Boolean = "Boolean",
}
export enum SourcingEventSortField {
  Title = "TITLE",
  ReferenceNumber = "REFERENCE_NUMBER",
  Deadline = "DEADLINE",
  CreatedAt = "CREATED_AT",
  ModifiedAt = "MODIFIED_AT",
}
export enum SortDirection {
  Asc = "ASC",
  Desc = "DESC",
}
export enum ContractStateDto {
  Draft = "Draft",
  Published = "Published",
}
export enum ContractStatusDto {
  Awarded = "Awarded",
  Active = "Active",
  Expired = "Expired",
  Terminated = "Terminated",
}
export enum ContractReportSortColumn {
  Id = "ID",
  Title = "TITLE",
  TemplateName = "TEMPLATE_NAME",
  CreatedAt = "CREATED_AT",
  ModifiedAt = "MODIFIED_AT",
  OwningOrganizationName = "OWNING_ORGANIZATION_NAME",
  InternalOwnerName = "INTERNAL_OWNER_NAME",
  CounterpartyName = "COUNTERPARTY_NAME",
  CounterpartyContactFullName = "COUNTERPARTY_CONTACT_FULL_NAME",
  CounterpartyContactEmail = "COUNTERPARTY_CONTACT_EMAIL",
  CounterpartyContactPhoneNumber = "COUNTERPARTY_CONTACT_PHONE_NUMBER",
  Status = "STATUS",
  State = "STATE",
  StartDate = "START_DATE",
  EndDate = "END_DATE",
  TotalPrice = "TOTAL_PRICE",
  EstimatedValue = "ESTIMATED_VALUE",
  Priority = "PRIORITY",
  Risk = "RISK",
  RenewalDate = "RENEWAL_DATE",
  PublishedAt = "PUBLISHED_AT",
  SupplierCategory = "SUPPLIER_CATEGORY",
  ReferenceNumber = "REFERENCE_NUMBER",
  Description = "DESCRIPTION",
  Currency = "CURRENCY",
  OriginSourcingEventName = "ORIGIN_SOURCING_EVENT_NAME",
  PublishedByName = "PUBLISHED_BY_NAME",
  TerminatedByName = "TERMINATED_BY_NAME",
  TerminationReason = "TERMINATION_REASON",
  WarrantyExpirationDate = "WARRANTY_EXPIRATION_DATE",
  NoticePeriod = "NOTICE_PERIOD",
  PaymentTermsInDays = "PAYMENT_TERMS_IN_DAYS",
  PricePerMonth = "PRICE_PER_MONTH",
  PricePerYear = "PRICE_PER_YEAR",
  TotalLiability = "TOTAL_LIABILITY",
  ExternalLink = "EXTERNAL_LINK",
  PurchasingPolicy = "PURCHASING_POLICY",
}
export enum ContractReportOptionParameter {
  ProjectName = "PROJECT_NAME",
  ProjectId = "PROJECT_ID",
  InternalPartyName = "INTERNAL_PARTY_NAME",
  InternalPartyId = "INTERNAL_PARTY_ID",
  LinkedContractTitle = "LINKED_CONTRACT_TITLE",
  LinkedContractId = "LINKED_CONTRACT_ID",
  TemplateName = "TEMPLATE_NAME",
  TemplateId = "TEMPLATE_ID",
  OwningOrganizationName = "OWNING_ORGANIZATION_NAME",
  OwningOrganizationId = "OWNING_ORGANIZATION_ID",
  InternalOwnerName = "INTERNAL_OWNER_NAME",
  InternalOwnerId = "INTERNAL_OWNER_ID",
  CounterpartyName = "COUNTERPARTY_NAME",
  CounterpartyId = "COUNTERPARTY_ID",
  CounterpartyContactFullName = "COUNTERPARTY_CONTACT_FULL_NAME",
  CounterpartyContactEmail = "COUNTERPARTY_CONTACT_EMAIL",
  CounterpartyContactPhoneNumber = "COUNTERPARTY_CONTACT_PHONE_NUMBER",
  Status = "STATUS",
  Risk = "RISK",
  Priority = "PRIORITY",
  SupplierCategory = "SUPPLIER_CATEGORY",
  ReferenceNumber = "REFERENCE_NUMBER",
  Description = "DESCRIPTION",
  Currency = "CURRENCY",
  OriginSourcingEventName = "ORIGIN_SOURCING_EVENT_NAME",
  OriginSourcingEventId = "ORIGIN_SOURCING_EVENT_ID",
  PublishedByName = "PUBLISHED_BY_NAME",
  PublishedById = "PUBLISHED_BY_ID",
  TerminatedByName = "TERMINATED_BY_NAME",
  TerminatedById = "TERMINATED_BY_ID",
  TerminationReason = "TERMINATION_REASON",
  StartDate = "START_DATE",
  EndDate = "END_DATE",
  CreatedAt = "CREATED_AT",
  ModifiedAt = "MODIFIED_AT",
  PublishedAt = "PUBLISHED_AT",
  RenewalDate = "RENEWAL_DATE",
  TerminationDate = "TERMINATION_DATE",
  WarrantyExpirationDate = "WARRANTY_EXPIRATION_DATE",
  NoticePeriod = "NOTICE_PERIOD",
  PaymentTermsInDays = "PAYMENT_TERMS_IN_DAYS",
  PricePerMonth = "PRICE_PER_MONTH",
  PricePerYear = "PRICE_PER_YEAR",
  TotalLiability = "TOTAL_LIABILITY",
  ExternalLink = "EXTERNAL_LINK",
  PurchasingPolicy = "PURCHASING_POLICY",
  DocumentId = "DOCUMENT_ID",
  SignedDocumentId = "SIGNED_DOCUMENT_ID",
  IndustryId = "INDUSTRY_ID",
  IsClimateCommitted = "IS_CLIMATE_COMMITTED",
  HasSignedCodeOfConduct = "HAS_SIGNED_CODE_OF_CONDUCT",
  HasCustomerContact = "HAS_CUSTOMER_CONTACT",
  OptionName = "OPTION_NAME",
}
export enum CommonTemplateType {
  ExpirationDate = "ExpirationDate",
}
export enum ContractPriority {
  VeryLow = "VeryLow",
  BelowAverage = "BelowAverage",
  Average = "Average",
  AboveAverage = "AboveAverage",
  VeryHigh = "VeryHigh",
}
export enum ContractRisk {
  VeryLow = "VeryLow",
  BelowAverage = "BelowAverage",
  Average = "Average",
  AboveAverage = "AboveAverage",
  VeryHigh = "VeryHigh",
}
export enum IfrsAdjustmentType {
  Monetary = "Monetary",
  Percentage = "Percentage",
}
export enum Category {
  A = "A",
  B = "B",
  C = "C",
  D = "D",
  E = "E",
}
export enum SignedDocumentStatus {
  Unsigned = "Unsigned",
  WaitingForAttachments = "WaitingForAttachments",
  PartiallySigned = "PartiallySigned",
  Signed = "Signed",
  Canceled = "Canceled",
  Expired = "Expired",
}
export enum DocumentSignerStatusDto {
  DocumentEmailNotOpened = "DocumentEmailNotOpened",
  DocumentEmailOpened = "DocumentEmailOpened",
  DocumentLinkOpened = "DocumentLinkOpened",
  DocumentRead = "DocumentRead",
  DocumentSigned = "DocumentSigned",
  EmailSendingFailed = "EmailSendingFailed",
}
export enum ContractNotificationTypeDto {
  EndDateReminder = "EndDateReminder",
  RenewalDateReminder = "RenewalDateReminder",
  WarrantyExpirationDateReminder = "WarrantyExpirationDateReminder",
  StartDateReminder = "StartDateReminder",
  TerminationDateReminder = "TerminationDateReminder",
  NoticePeriodReminder = "NoticePeriodReminder",
  OptionExecutionDeadlineReminder = "OptionExecutionDeadlineReminder",
  OptionStartDateReminder = "OptionStartDateReminder",
  NoEndDateReminder = "NoEndDateReminder",
}
export enum ContractNotificationDurationType {
  Days = "Days",
  Months = "Months",
}
export enum ContractTaskStatus {
  Open = "Open",
  Completed = "Completed",
}
export enum ContractTaskStatusDto {
  Open = "Open",
  Completed = "Completed",
  Overdue = "Overdue",
}
export enum ContractTaskResponsiblePartyDto {
  Organization = "Organization",
  PersonInOrganization = "PersonInOrganization",
}
export enum ContractTaskChangeType {
  Description = "Description",
  DueDate = "DueDate",
  ResponsibleParty = "ResponsibleParty",
  Status = "Status",
  Title = "Title",
}
export enum ContractRoleType {
  Editor = "Editor",
  Viewer = "Viewer",
  Counterparty = "Counterparty",
}
export enum NotificationTypeDto {
  ContractTaskCommentAdded = "ContractTaskCommentAdded",
  ContractTaskDelegated = "ContractTaskDelegated",
  ContractTaskDeleted = "ContractTaskDeleted",
  ContractTaskDueDateUpdated = "ContractTaskDueDateUpdated",
  ContractTaskStatusUpdated = "ContractTaskStatusUpdated",
  ContractTaskDueDateReminder = "ContractTaskDueDateReminder",
  ContractSharedWithOrg = "ContractSharedWithOrg",
  ContractSharedWithDepartment = "ContractSharedWithDepartment",
  ContractSharedWithPerson = "ContractSharedWithPerson",
  ContractDocumentCompletedSigning = "ContractDocumentCompletedSigning",
  ContractSignatureDeadlineExpired = "ContractSignatureDeadlineExpired",
  ContractDateReminder = "ContractDateReminder",
  SourcingEventOrganizationInvite = "SourcingEventOrganizationInvite",
  NegotiationInvite = "NegotiationInvite",
  SourcingEventAnnouncement = "SourcingEventAnnouncement",
  SourcingEventAwarded = "SourcingEventAwarded",
  SourcingEventNewQuestion = "SourcingEventNewQuestion",
  SourcingEventNewQuestionComment = "SourcingEventNewQuestionComment",
  SourcingEventQuestionStatusUpdated = "SourcingEventQuestionStatusUpdated",
  SourcingEventCancelled = "SourcingEventCancelled",
  SourcingEventClosed = "SourcingEventClosed",
  BidDeliveryDeadline = "BidDeliveryDeadline",
  BidDelivered = "BidDelivered",
}
export enum ReceiverRoleDto {
  Supplier = "Supplier",
  Buyer = "Buyer",
}
export enum ContractDateReminderType {
  EndDateReminder = "EndDateReminder",
  WarrantyExpirationDateReminder = "WarrantyExpirationDateReminder",
  RenewalDateReminder = "RenewalDateReminder",
  StartDateReminder = "StartDateReminder",
  TerminationDateReminder = "TerminationDateReminder",
  NoticePeriodReminder = "NoticePeriodReminder",
  OptionExecutionDeadlineReminder = "OptionExecutionDeadlineReminder",
  OptionStartDateReminder = "OptionStartDateReminder",
  NoEndDateReminder = "NoEndDateReminder",
}
export enum EmailInviteTypeDto {
  Supplier = "Supplier",
  SourcingEvent = "SourcingEvent",
  OrgUser = "OrgUser",
}
export enum ContractOwnerContextType {
  Common = "Common",
  OrganizationOwner = "OrganizationOwner",
}
export enum DocumentSignerMethod {
  NoBankId = "NoBankId",
  Handwritten = "Handwritten",
}
export enum PatchOperationType {
  Replace = "Replace",
  Add = "Add",
  Remove = "Remove",
}
export enum SchemaAttributeType {
  String = "string",
  Boolean = "boolean",
  Complex = "complex",
  Reference = "reference",
}
export enum SchemaAttributeMutability {
  ReadWrite = "readWrite",
  ReadOnly = "readOnly",
  Immutable = "immutable",
}
export enum SchemaAttributeUniqueness {
  None = "none",
  Server = "server",
}
export enum ScimResourceType {
  User = "User",
  Group = "Group",
}
export enum BseBidStatusDto {
  InviteNotAccepted = "InviteNotAccepted",
  InviteAccepted = "InviteAccepted",
  InviteDeclined = "InviteDeclined",
  DraftCreated = "DraftCreated",
  BidDelivered = "BidDelivered",
  DeadlinePassedAndBidDelivered = "DeadlinePassedAndBidDelivered",
  DeadlinePassedWithoutBidDelivered = "DeadlinePassedWithoutBidDelivered",
  IsWinner = "IsWinner",
  IsLoser = "IsLoser",
  EventCancelledAndBidDelivered = "EventCancelledAndBidDelivered",
  EventCancelledWithoutBidDelivered = "EventCancelledWithoutBidDelivered",
  EventClosedAndBidDelivered = "EventClosedAndBidDelivered",
  EventClosedWithoutBidDelivered = "EventClosedWithoutBidDelivered",
}
export const {
  useAdminListAllEntitiesQuery,
  useAdminListOrganizationsQuery,
  useAdminUpdateOrganizationMutation,
  useSendAiChatMessageMutation,
  useCreateAssessmentTemplateMutation,
  useGetAssessmentTemplateQuery,
  useCreateAssessmentMutation,
  useGetAssessmentQuery,
  useGetBaseDataQuery,
  useGetTypesQuery,
  useCreateBasicSourcingEventMutation,
  useGetNegotiationRoundQuery,
  useGetBasicSourcingEventQuery,
  useUpdateBasicSourcingEventMutation,
  useAwardToBidMutation,
  useListReceivedBidsQuery,
  useGetReceivedBseBidQuery,
  useCancelSourcingEventMutation,
  useCloseSourcingEventMutation,
  useCompleteEvaluationMutation,
  useCreateSourcingEventCopyMutation,
  useCreateUploadForSourcingEventDescriptionMutation,
  useCreateEmailInviteReminderMutation,
  useCreateNegotiationRoundMutation,
  useOpenCompletedEventMutation,
  usePublishBasicSourcingEventMutation,
  useCreateBseQuestionAsBuyerMutation,
  useUpdateBseQuestionMutation,
  useCreateBseQuestionCommentAsBuyerMutation,
  useCreateUploadForBseMutation,
  useEvaluateSingleBidWithAiMutation,
  useEvaluateBidsWithAiMutation,
  useGenerateRequirementsWithAiMutation,
  useDeleteSourcingEventMutation,
  useCreateOrganizationInviteEmailReminderMutation,
  useCompleteNegotiationRoundEvaluationMutation,
  useOpenNegotiationRoundMutation,
  usePublishNegotiationRoundMutation,
  useEditNegotiationRoundMutation,
  useCreateUploadForNegotiationRoundDescriptionMutation,
  useCreateUploadForNegotiationRoundMutation,
  useGetProductGroupsForSourcingEventQuery,
  useListEventsForBuyerQuery,
  useListContractReportsQuery,
  useListContractReportOptionsQuery,
  useCreateContractFromSourcingEventMutation,
  useDeleteCustomContractDataFieldMutation,
  useUpsertCustomContractDataFieldMutation,
  useUpsertContractDataFieldsSectionMutation,
  useContractReportQueryDummyMutation,
  useSignatureWebhookMutation,
  useDeleteContractTaskCommentMutation,
  useUpdateContractTaskCommentMutation,
  useDeleteContractTaskMutation,
  useUpdateContractTaskMutation,
  useListChangesForContractTaskQuery,
  useListCommentsForContractTaskQuery,
  useCreateContractTaskCommentMutation,
  useCreateContractTextDocumentMutation,
  useListTextDocumentsForContractQuery,
  useDownloadMultiIfrsReportQuery,
  useGetContractStatisticsQuery,
  useCancelSignedDocumentMutation,
  useCreateUploadForContractDescriptionMutation,
  useDownloadIfrsReportQuery,
  useDeleteContractParticipantMutation,
  useListParticipantsForContractsQuery,
  useCreateContractParticipantMutation,
  useCreateContractTaskMutation,
  useCreateUploadForContractMutation,
  useListContractFilterOptionsQuery,
  useListContractsQuery,
  useStartDocumentChatMutation,
  useUpdateDocumentMetaMutation,
  useGetDownloadLinkQuery,
  useListDocumentTemplatesQuery,
  useCreateDocumentTemplateMutation,
  useDownloadTextDocumentMutation,
  useUpdateEmailStatusMutation,
  useConfirmUploadMutation,
  useSearchIndustriesQuery,
  useListMyNotificationsQuery,
  useListOrganizationUsersQuery,
  useListOrganizationsForMeQuery,
  useCreateOrganizationForMeMutation,
  useMarkNotificationsAsPeekedMutation,
  useMarkNotificationsAsViewedMutation,
  useCreateNegotiationTextDocumentMutation,
  useListTextDocumentsForNegotiationRoundQuery,
  useGetAccessTokenMutation,
  useSearchOpenOrgContactInfoQuery,
  useListContractsForOrgQuery,
  useCreateApiClientMutation,
  useCreateOrganizationEntryMutation,
  useGetOrganizationEntryQuery,
  useUpdateOrganizationEntryMutation,
  useLinkOrganizationEntryMutation,
  useGetSupplierEmailInviteQuery,
  useGetOrgUserEmailInviteQuery,
  useAcceptOrgUserEmailInviteMutation,
  useListAssessmentTemplatesQuery,
  useUpdateAssessmentMutation,
  useListAssessmentsQuery,
  useUpdateOrganizationMutation,
  useGetOrganizationQuery,
  useListSupplierEmailInvitesQuery,
  useCreateSupplierEmailInviteMutation,
  useDeleteSupplierEmailInviteMutation,
  useCreateSupplierEmailInviteReminderMutation,
  useUpdateOrganizationTenantMutation,
  useListOrgUserEmailInvitesQuery,
  useCreateOrgUserEmailInviteMutation,
  useDeleteOrgUserEmailInviteMutation,
  useDeleteOrgUserMutation,
  useUpdateOrgUserRoleMutation,
  useListContractTasksForOrganizationQuery,
  useListContractTemplatesQuery,
  useCreateContractTemplateMutation,
  useGetTaskQuery,
  useUpdateContractTemplateMutation,
  useGetContractQuery,
  useUpdateContractMutation,
  usePublishContractMutation,
  useCreateSignedDocumentMutation,
  useCreateSignedTextDocumentMutation,
  useListContractTasksQuery,
  useCreateContractMutation,
  useListPersonsQuery,
  useGetPersonsByIdsQuery,
  useGetMeQuery,
  useUpdateMeMutation,
  useEditProductGroupMutation,
  useCreateProductGroupMutation,
  useDeleteProductGroupMutation,
  useGetProductGroupsForProjectQuery,
  useGetProductTemplatesQuery,
  useUpdateProjectMutation,
  useCreateProjectMutation,
  useCreateProjectTextDocumentMutation,
  useListTextDocumentsForProjectQuery,
  useGetProjectQuery,
  useGetProjectsQuery,
  useGetProjectsByIdsQuery,
  useCreateUploadForProjectDescriptionMutation,
  useCreateUploadForProjectMutation,
  useGetGroupsQuery,
  useCreateGroupMutation,
  useDeleteGroupMutation,
  useGetGroupQuery,
  useUpdateGroupMutation,
  useGetSchemasQuery,
  useGetTokenQuery,
  useGetUsersQuery,
  useCreateUserMutation,
  useDeleteUserMutation,
  useGetUserQuery,
  useUpdateUserMutation,
  useSearchOrganizationsAndEntriesQuery,
  useUpdateSignedDocumentMetaMutation,
  useGetSignedDocumentDownloadLinkQuery,
  useGetSignedAttachmentDownloadLinkQuery,
  useCreateSourcingEventTextDocumentMutation,
  useListTextDocumentsForSourcingEventQuery,
  useGetBseBidQuery,
  useUpdateBseBidMutation,
  useCreateUploadForBseBidDescriptionMutation,
  useCreateUploadForBseBidMutation,
  useGetBseSupplierViewQuery,
  useCreateBseAnnouncementMutation,
  useUnDeliverBseBidMutation,
  useAcceptInvitationToBidMutation,
  useDeclineInvitationToBidMutation,
  useDownloadPublicProcurementProtocolPartOneMutation,
  useDownloadQaReportQuery,
  useCreateBseQuestionAsSupplierMutation,
  useCreateBseQuestionCommentAsSupplierMutation,
  useMarkAsViewedMutation,
  useCreateDraftBidMutation,
  useDeleteBidMutation,
  useListBidsCreatedByDepartmentQuery,
  useGetSeEmailInviteQuery,
  useAcceptSeEmailInviteMutation,
  useDeliverBidMutation,
  useListSeSupplierViewsQuery,
  useGetProductGroupsAsSupplierQuery,
  useUpdateTextDocumentMutation,
  useDeleteTextDocumentMutation,
  useCreateUploadForTextDocumentMutation,
  useDeleteUploadMutation,
} = injectedRtkApi;
